import React, { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { PublicClientApplication } from '@azure/msal-browser';
import './RFQDashboard_Sendify.css';

// ─── MICROSOFT 365 (ENTRA) SSO ────────────────────────────────────────────────
// Per-person sign-in with the worker's @gtshipbrokers.com account. The single-
// tenant app registration restricts authentication to our tenant at Microsoft;
// the domain check below is defence-in-depth. clientId/tenantId are PUBLIC OAuth
// identifiers (visible in the redirect URL), not secrets — safe to ship in the SPA.
const MSAL_CLIENT_ID = process.env.REACT_APP_MSAL_CLIENT_ID || '7f68132d-d8fb-4259-80b8-8e89e7602b5f';
const MSAL_TENANT_ID = process.env.REACT_APP_MSAL_TENANT_ID || 'da33e900-6fdf-45b9-b04b-0d5ac617cffd';
const msalConfigured = !!(MSAL_CLIENT_ID && MSAL_TENANT_ID);
const msalInstance = msalConfigured
  ? new PublicClientApplication({
      auth: {
        clientId: MSAL_CLIENT_ID,
        authority: `https://login.microsoftonline.com/${MSAL_TENANT_ID}`,
        redirectUri: typeof window !== 'undefined' ? window.location.origin : '/',
      },
      cache: { cacheLocation: 'localStorage' },
    })
  : null;
let msalReady = null; // memoised initialize() promise (MSAL v3+ requires it)
const ensureMsal = () => {
  if (!msalInstance) return Promise.reject(new Error('SSO not configured'));
  if (!msalReady) msalReady = msalInstance.initialize();
  return msalReady;
};

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const ALL_SERVICES = ['FTL','LTL','GROUPAGE','FCL','LCL','AIR','EXPRESS','OOG','NFO','OBC','CUSTOMS'];
const TRP_MODES = ['ROAD FREIGHT','AIR FREIGHT','SEA FREIGHT','EXPRESS SERVICE','OBC','RAIL FREIGHT','CUSTOMS CLEARENCE'];
const STATUS_LABELS = ['New shipment','IN PROGRESS','TO BE INVOICED','DELIVERED','INVOICED','ON HOLD','CANCELLED'];
// Body of the follow-up "reminder" email sent to carriers who haven't replied to
// the original RFQ. The greeting ("Hi,") and the quoted original thread are added
// automatically by the add-in — this is just the message wording.
const DEFAULT_REMINDER_TEMPLATE = `Following up on our RFQ below — did you receive it? We'd appreciate your best price.\n\nThank you!`;

// Common cities/ports → [lat, lng] for instant route plotting; unknowns fall back
// to OpenStreetMap (Nominatim) geocoding so any place still resolves.
const CITY_COORDS = {
  tallinn:[59.437,24.754], muuga:[59.49,24.95], paldiski:[59.35,24.05], rakvere:[59.347,26.356],
  parnu:[58.385,24.497], narva:[59.377,28.19], rotterdam:[51.924,4.477], amsterdam:[52.370,4.895],
  antwerp:[51.222,4.400], antwerpen:[51.222,4.400], hamburg:[53.551,9.993], bremerhaven:[53.540,8.580],
  bremen:[53.079,8.802], helsinki:[60.169,24.938], kotka:[60.466,26.945], turku:[60.451,22.267],
  stockholm:[59.329,18.069], gothenburg:[57.709,11.974], goteborg:[57.709,11.974], oslo:[59.914,10.752],
  copenhagen:[55.676,12.568], riga:[56.949,24.105], ventspils:[57.394,21.564], klaipeda:[55.703,21.144],
  vilnius:[54.687,25.280], gdansk:[54.352,18.646], gdynia:[54.518,18.531], warsaw:[52.230,21.012],
  warszawa:[52.230,21.012], berlin:[52.520,13.405], frankfurt:[50.110,8.682], munich:[48.135,11.582],
  london:[51.507,-0.128], felixstowe:[51.964,1.351], immingham:[53.614,-0.220], paris:[48.857,2.352],
  lehavre:[49.494,0.108], 'le havre':[49.494,0.108], madrid:[40.417,-3.703], barcelona:[41.385,2.173],
  milan:[45.464,9.190], sassuolo:[44.548,10.783], genoa:[44.406,8.946], rome:[41.903,12.496],
  vienna:[48.208,16.373], prague:[50.075,14.437], maputo:[-25.966,32.580], casablanca:[33.573,-7.590],
};

async function geocodeCity(name) {
  if (!name) return null;
  const key = String(name).split(',')[0].trim().toLowerCase();
  if (CITY_COORDS[key]) return CITY_COORDS[key];
  try {
    const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(name)}`);
    const j = await r.json();
    if (Array.isArray(j) && j[0]) return [parseFloat(j[0].lat), parseFloat(j[0].lon)];
  } catch { /* offline / rate-limited — silently skip */ }
  return null;
}

const COUNTRIES = [
  { code: 'AF', name: 'Afghanistan' },
  { code: 'AX', name: 'Åland Islands' },
  { code: 'AL', name: 'Albania' },
  { code: 'DZ', name: 'Algeria' },
  { code: 'AS', name: 'American Samoa' },
  { code: 'AD', name: 'Andorra' },
  { code: 'AO', name: 'Angola' },
  { code: 'AI', name: 'Anguilla' },
  { code: 'AQ', name: 'Antarctica' },
  { code: 'AG', name: 'Antigua and Barbuda' },
  { code: 'AR', name: 'Argentina' },
  { code: 'AM', name: 'Armenia' },
  { code: 'AW', name: 'Aruba' },
  { code: 'AU', name: 'Australia' },
  { code: 'AT', name: 'Austria' },
  { code: 'AZ', name: 'Azerbaijan' },
  { code: 'BS', name: 'Bahamas' },
  { code: 'BH', name: 'Bahrain' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'BB', name: 'Barbados' },
  { code: 'BY', name: 'Belarus' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BZ', name: 'Belize' },
  { code: 'BJ', name: 'Benin' },
  { code: 'BM', name: 'Bermuda' },
  { code: 'BT', name: 'Bhutan' },
  { code: 'BO', name: 'Bolivia' },
  { code: 'BQ', name: 'Bonaire, Sint Eustatius and Saba' },
  { code: 'BA', name: 'Bosnia and Herzegovina' },
  { code: 'BW', name: 'Botswana' },
  { code: 'BV', name: 'Bouvet Island' },
  { code: 'BR', name: 'Brazil' },
  { code: 'IO', name: 'British Indian Ocean Territory' },
  { code: 'BN', name: 'Brunei' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'BF', name: 'Burkina Faso' },
  { code: 'BI', name: 'Burundi' },
  { code: 'CV', name: 'Cabo Verde' },
  { code: 'KH', name: 'Cambodia' },
  { code: 'CM', name: 'Cameroon' },
  { code: 'CA', name: 'Canada' },
  { code: 'KY', name: 'Cayman Islands' },
  { code: 'CF', name: 'Central African Republic' },
  { code: 'TD', name: 'Chad' },
  { code: 'CL', name: 'Chile' },
  { code: 'CN', name: 'China' },
  { code: 'CX', name: 'Christmas Island' },
  { code: 'CC', name: 'Cocos (Keeling) Islands' },
  { code: 'CO', name: 'Colombia' },
  { code: 'KM', name: 'Comoros' },
  { code: 'CG', name: 'Congo' },
  { code: 'CD', name: 'Congo (DRC)' },
  { code: 'CK', name: 'Cook Islands' },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'CI', name: "Côte d'Ivoire" },
  { code: 'HR', name: 'Croatia' },
  { code: 'CU', name: 'Cuba' },
  { code: 'CW', name: 'Curaçao' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'CZ', name: 'Czechia' },
  { code: 'DK', name: 'Denmark' },
  { code: 'DJ', name: 'Djibouti' },
  { code: 'DM', name: 'Dominica' },
  { code: 'DO', name: 'Dominican Republic' },
  { code: 'EC', name: 'Ecuador' },
  { code: 'EG', name: 'Egypt' },
  { code: 'SV', name: 'El Salvador' },
  { code: 'GQ', name: 'Equatorial Guinea' },
  { code: 'ER', name: 'Eritrea' },
  { code: 'EE', name: 'Estonia' },
  { code: 'SZ', name: 'Eswatini' },
  { code: 'ET', name: 'Ethiopia' },
  { code: 'FK', name: 'Falkland Islands' },
  { code: 'FO', name: 'Faroe Islands' },
  { code: 'FJ', name: 'Fiji' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'GF', name: 'French Guiana' },
  { code: 'PF', name: 'French Polynesia' },
  { code: 'TF', name: 'French Southern Territories' },
  { code: 'GA', name: 'Gabon' },
  { code: 'GM', name: 'Gambia' },
  { code: 'GE', name: 'Georgia' },
  { code: 'DE', name: 'Germany' },
  { code: 'GH', name: 'Ghana' },
  { code: 'GI', name: 'Gibraltar' },
  { code: 'GR', name: 'Greece' },
  { code: 'GL', name: 'Greenland' },
  { code: 'GD', name: 'Grenada' },
  { code: 'GP', name: 'Guadeloupe' },
  { code: 'GU', name: 'Guam' },
  { code: 'GT', name: 'Guatemala' },
  { code: 'GG', name: 'Guernsey' },
  { code: 'GN', name: 'Guinea' },
  { code: 'GW', name: 'Guinea-Bissau' },
  { code: 'GY', name: 'Guyana' },
  { code: 'HT', name: 'Haiti' },
  { code: 'HM', name: 'Heard Island and McDonald Islands' },
  { code: 'VA', name: 'Holy See' },
  { code: 'HN', name: 'Honduras' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'HU', name: 'Hungary' },
  { code: 'IS', name: 'Iceland' },
  { code: 'IN', name: 'India' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'IR', name: 'Iran' },
  { code: 'IQ', name: 'Iraq' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IM', name: 'Isle of Man' },
  { code: 'IL', name: 'Israel' },
  { code: 'IT', name: 'Italy' },
  { code: 'JM', name: 'Jamaica' },
  { code: 'JP', name: 'Japan' },
  { code: 'JE', name: 'Jersey' },
  { code: 'JO', name: 'Jordan' },
  { code: 'KZ', name: 'Kazakhstan' },
  { code: 'KE', name: 'Kenya' },
  { code: 'KI', name: 'Kiribati' },
  { code: 'KP', name: 'North Korea' },
  { code: 'KR', name: 'South Korea' },
  { code: 'XK', name: 'Kosovo' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'KG', name: 'Kyrgyzstan' },
  { code: 'LA', name: 'Laos' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LB', name: 'Lebanon' },
  { code: 'LS', name: 'Lesotho' },
  { code: 'LR', name: 'Liberia' },
  { code: 'LY', name: 'Libya' },
  { code: 'LI', name: 'Liechtenstein' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MO', name: 'Macao' },
  { code: 'MG', name: 'Madagascar' },
  { code: 'MW', name: 'Malawi' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'MV', name: 'Maldives' },
  { code: 'ML', name: 'Mali' },
  { code: 'MT', name: 'Malta' },
  { code: 'MH', name: 'Marshall Islands' },
  { code: 'MQ', name: 'Martinique' },
  { code: 'MR', name: 'Mauritania' },
  { code: 'MU', name: 'Mauritius' },
  { code: 'YT', name: 'Mayotte' },
  { code: 'MX', name: 'Mexico' },
  { code: 'FM', name: 'Micronesia' },
  { code: 'MD', name: 'Moldova' },
  { code: 'MC', name: 'Monaco' },
  { code: 'MN', name: 'Mongolia' },
  { code: 'ME', name: 'Montenegro' },
  { code: 'MS', name: 'Montserrat' },
  { code: 'MA', name: 'Morocco' },
  { code: 'MZ', name: 'Mozambique' },
  { code: 'MM', name: 'Myanmar' },
  { code: 'NA', name: 'Namibia' },
  { code: 'NR', name: 'Nauru' },
  { code: 'NP', name: 'Nepal' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'NC', name: 'New Caledonia' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'NI', name: 'Nicaragua' },
  { code: 'NE', name: 'Niger' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'NU', name: 'Niue' },
  { code: 'NF', name: 'Norfolk Island' },
  { code: 'MK', name: 'North Macedonia' },
  { code: 'MP', name: 'Northern Mariana Islands' },
  { code: 'NO', name: 'Norway' },
  { code: 'OM', name: 'Oman' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'PW', name: 'Palau' },
  { code: 'PS', name: 'Palestine' },
  { code: 'PA', name: 'Panama' },
  { code: 'PG', name: 'Papua New Guinea' },
  { code: 'PY', name: 'Paraguay' },
  { code: 'PE', name: 'Peru' },
  { code: 'PH', name: 'Philippines' },
  { code: 'PN', name: 'Pitcairn' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'QA', name: 'Qatar' },
  { code: 'RE', name: 'Réunion' },
  { code: 'RO', name: 'Romania' },
  { code: 'RU', name: 'Russia' },
  { code: 'RW', name: 'Rwanda' },
  { code: 'BL', name: 'Saint Barthélemy' },
  { code: 'SH', name: 'Saint Helena' },
  { code: 'KN', name: 'Saint Kitts and Nevis' },
  { code: 'LC', name: 'Saint Lucia' },
  { code: 'MF', name: 'Saint Martin' },
  { code: 'PM', name: 'Saint Pierre and Miquelon' },
  { code: 'VC', name: 'Saint Vincent and the Grenadines' },
  { code: 'WS', name: 'Samoa' },
  { code: 'SM', name: 'San Marino' },
  { code: 'ST', name: 'Sao Tome and Principe' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'SN', name: 'Senegal' },
  { code: 'RS', name: 'Serbia' },
  { code: 'SC', name: 'Seychelles' },
  { code: 'SL', name: 'Sierra Leone' },
  { code: 'SG', name: 'Singapore' },
  { code: 'SX', name: 'Sint Maarten' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'SB', name: 'Solomon Islands' },
  { code: 'SO', name: 'Somalia' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'GS', name: 'South Georgia' },
  { code: 'SS', name: 'South Sudan' },
  { code: 'ES', name: 'Spain' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'SD', name: 'Sudan' },
  { code: 'SR', name: 'Suriname' },
  { code: 'SJ', name: 'Svalbard and Jan Mayen' },
  { code: 'SE', name: 'Sweden' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'SY', name: 'Syria' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'TJ', name: 'Tajikistan' },
  { code: 'TZ', name: 'Tanzania' },
  { code: 'TH', name: 'Thailand' },
  { code: 'TL', name: 'Timor-Leste' },
  { code: 'TG', name: 'Togo' },
  { code: 'TK', name: 'Tokelau' },
  { code: 'TO', name: 'Tonga' },
  { code: 'TT', name: 'Trinidad and Tobago' },
  { code: 'TN', name: 'Tunisia' },
  { code: 'TR', name: 'Turkey' },
  { code: 'TM', name: 'Turkmenistan' },
  { code: 'TC', name: 'Turks and Caicos Islands' },
  { code: 'TV', name: 'Tuvalu' },
  { code: 'UG', name: 'Uganda' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'UM', name: 'United States Minor Outlying Islands' },
  { code: 'US', name: 'United States' },
  { code: 'UY', name: 'Uruguay' },
  { code: 'UZ', name: 'Uzbekistan' },
  { code: 'VU', name: 'Vanuatu' },
  { code: 'VE', name: 'Venezuela' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'VG', name: 'Virgin Islands (British)' },
  { code: 'VI', name: 'Virgin Islands (US)' },
  { code: 'WF', name: 'Wallis and Futuna' },
  { code: 'EH', name: 'Western Sahara' },
  { code: 'YE', name: 'Yemen' },
  { code: 'ZM', name: 'Zambia' },
  { code: 'ZW', name: 'Zimbabwe' },
];

// ─── COUNTRY COMBOBOX ─────────────────────────────────────────────────────────
const CountryCombobox = ({ value, onChange, inputStyle }) => {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen]   = useState(false);
  const wrapRef = useRef(null);

  // Keep query in sync when parent changes value (e.g. openOrderModal pre-fills)
  useEffect(() => { setQuery(value || ''); }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = COUNTRIES.filter(c => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return c.code.toLowerCase().startsWith(q) || c.name.toLowerCase().includes(q);
  }).slice(0, 10);

  const handleSelect = c => {
    onChange(c.code);
    setQuery(c.code);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); onChange(e.target.value); }}
        onFocus={() => setOpen(true)}
        placeholder="e.g. NL"
        style={inputStyle}
        autoComplete="off"
      />
      {open && filtered.length > 0 && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 2px)', left: 0, right: 0,
          background: '#fff', border: '1px solid #d1d5db',
          borderRadius: '6px', boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
          zIndex: 9999, maxHeight: '220px', overflowY: 'auto',
        }}>
          {filtered.map(c => (
            <div
              key={c.code}
              onMouseDown={e => { e.preventDefault(); handleSelect(c); }}
              style={{
                padding: '7px 12px', cursor: 'pointer', display: 'flex',
                alignItems: 'center', gap: '10px', fontSize: '13px',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#f0f4ff'}
              onMouseLeave={e => e.currentTarget.style.background = ''}
            >
              <span style={{ fontWeight: '700', minWidth: '28px', color: '#0F2746', fontFamily: 'monospace', fontSize: '12px' }}>{c.code}</span>
              <span style={{ color: '#374151' }}>{c.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── ADDRESS AUTOCOMPLETE (Nominatim) ─────────────────────────────────────────
const AddressAutocomplete = ({ value, onChange, onCountryChange, placeholder, style, fullAddress = false }) => {
  const [suggs, setSuggs] = useState([]);
  const [open, setOpen]   = useState(false);
  const timer = useRef(null);
  const wrap  = useRef(null);

  const search = (q) => {
    if (!q || q.length < 2) { setSuggs([]); setOpen(false); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const res  = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=6&addressdetails=1`,
          { headers: { 'Accept-Language': 'en' } }
        );
        const data = await res.json();
        const seen = new Set();
        const items = [];
        for (const r of data) {
          const a  = r.address || {};
          const city    = a.city || a.town || a.village || a.municipality || a.hamlet || r.name || '';
          const country = a.country || '';
          const cc      = (a.country_code || '').toUpperCase();
          const label   = fullAddress ? r.display_name : [city, country].filter(Boolean).join(', ');
          if (label && !seen.has(label)) { seen.add(label); items.push({ label, city, country, cc }); }
        }
        setSuggs(items.slice(0, 5));
        setOpen(items.length > 0);
      } catch { setOpen(false); }
    }, 350);
  };

  useEffect(() => {
    const h = e => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div ref={wrap} style={{ position: 'relative' }}>
      <input type="text" value={value || ''} placeholder={placeholder} style={style} autoComplete="off"
        onChange={e => { onChange(e.target.value); search(e.target.value); }}
        onFocus={() => { if (suggs.length) setOpen(true); }}
      />
      {open && suggs.length > 0 && (
        <ul style={{
          position:'absolute', top:'100%', left:0, right:0, zIndex:1000,
          margin:0, padding:0, listStyle:'none',
          background:'#fff', border:'1px solid #D0DCE9', borderRadius:'0 0 7px 7px',
          boxShadow:'0 6px 18px rgba(0,0,0,.14)', maxHeight:'180px', overflowY:'auto',
        }}>
          {suggs.map((s, i) => (
            <li key={i}
              style={{ padding:'7px 12px', cursor:'pointer', fontSize:'12.5px', display:'flex', gap:'5px',
                borderBottom: i < suggs.length - 1 ? '1px solid #f0f4f8' : 'none', background:'#fff' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
              onMouseDown={e => {
                e.preventDefault();
                onChange(s.label);
                if (onCountryChange && s.cc) onCountryChange(s.cc);
                setOpen(false); setSuggs([]);
              }}
            >
              <strong style={{ color:'#0F2746' }}>
                {fullAddress ? s.label.split(',')[0] : s.city}
              </strong>
              <span style={{ color:'#6b7280' }}>
                {fullAddress ? s.label.split(',').slice(1, 3).join(',').trim() : s.country}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ─── AUTH / IDENTITY ──────────────────────────────────────────────────────────
// App-level identity for "who owns which RFQ" + the My/All manager scope.
// NOTE: this is NOT the security boundary — production access is gated separately
// by the Microsoft 365 / reverse-proxy sign-in. This only labels ownership.
const USER_REGISTRY = {
  // email (lowercase) → { name, role }.  role: 'admin' (sees all) | 'manager'
  // Admin is intentionally a single mailbox — only Mairold sees/does everything.
  'mairold.suuder@gtshipbrokers.com': { name: 'Mairold Suuder', role: 'admin' },
};
// Only these email domains may sign in (company staff). Soft client-side gate on
// top of the shared reverse-proxy password — see the auth note above.
const ALLOWED_EMAIL_DOMAINS = ['gtshipbrokers.com'];
const DEMO_USERS = [
  { name: 'Mairold Suuder', email: 'mairold.suuder@gtshipbrokers.com', role: 'admin' },
  { name: 'Taavi',  email: 'taavi@gtshipbrokers.com',  role: 'manager' },
  { name: 'Karlis', email: 'karlis@gtshipbrokers.com', role: 'manager' },
  { name: 'Ralf',   email: 'ralf@gtshipbrokers.com',   role: 'manager' },
];
const userInitials = (name) => (name || '?').trim().split(/\s+/).map(p => p[0]).slice(0, 2).join('').toUpperCase();
const userColor = (s) => { let h = 0; for (let i = 0; i < (s || '').length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return `hsl(${h % 360} 52% 40%)`; };
const resolveUser = (email, fallbackName) => {
  const key = (email || '').trim().toLowerCase();
  const reg = USER_REGISTRY[key];
  return {
    email: key,
    name: reg?.name || fallbackName ||
      (key ? key.split('@')[0].replace(/[._]/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'User'),
    role: reg?.role || 'manager',
  };
};

const LoginScreen = ({ onSignIn }) => {
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = (e) => {
    if (e) e.preventDefault();
    const v = email.trim().toLowerCase();
    if (!v) return;
    const domain = v.split('@')[1] || '';
    if (!ALLOWED_EMAIL_DOMAINS.includes(domain)) {
      setErr('Use your @gtshipbrokers.com work email.');
      return;
    }
    setErr('');
    onSignIn(resolveUser(v));
  };
  // Primary path: Microsoft 365 via REDIRECT (not popup — popups are blocked when
  // the dashboard tab has a window.opener). The page navigates to Microsoft and the
  // response is handled on return by handleRedirectPromise() in the main component.
  // The single-tenant app registration restricts auth to our tenant.
  const signInWithMicrosoft = async () => {
    setErr(''); setBusy(true);
    try {
      await ensureMsal();
      await msalInstance.loginRedirect({ scopes: ['User.Read'], prompt: 'select_account' });
      // navigation happens here; nothing below runs on success
    } catch (e) {
      setBusy(false);
      setErr('Microsoft sign-in failed: ' + (e?.message || e));
    }
  };
  // The email form + quick buttons bypass Microsoft — keep them off the production
  // login, available only on localhost or via ?dev for testing / fallback.
  const showDevLogin = typeof window !== 'undefined'
    && (window.location.hostname === 'localhost' || /[?&]dev\b/.test(window.location.search));
  return (
    <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(160deg,#0F2746 0%,#0c2240 100%)', zIndex: 2000, padding: '20px' }}>
      <div style={{ width: '100%', maxWidth: '380px', background: '#fff', borderRadius: '16px', boxShadow: '0 24px 60px rgba(0,0,0,0.35)', overflow: 'hidden' }}>
        <div style={{ background: '#0F2746', padding: '22px 24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img src="/gt-logo.svg" alt="GT-Shipbrokers" style={{ height: '38px' }} />
          <div>
            <div style={{ color: '#fff', fontWeight: 800, fontSize: '16px', letterSpacing: '-0.3px' }}>RFQ Manager</div>
            <div style={{ color: '#93c5fd', fontSize: '11px', fontWeight: 700, letterSpacing: '0.6px' }}>GT-SHIPBROKERS</div>
          </div>
        </div>
        <div style={{ padding: '24px' }}>
          {msalConfigured && (
            <button type="button" onClick={signInWithMicrosoft} disabled={busy}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                padding: '11px 14px', borderRadius: '8px', border: '1px solid #d1d5db', background: '#fff',
                color: '#1f2937', fontSize: '14px', fontWeight: 600, cursor: busy ? 'wait' : 'pointer' }}>
              <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true" style={{ flexShrink: 0 }}>
                <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
              </svg>
              {busy ? 'Signing in…' : 'Sign in with Microsoft'}
            </button>
          )}
          {err && <p style={{ fontSize: '12px', color: '#dc2626', margin: '10px 0 0', fontWeight: 600 }}>{err}</p>}
          <p style={{ fontSize: '11px', color: '#9ca3af', margin: '12px 0 4px', textAlign: 'center', lineHeight: 1.5 }}>
            Restricted to GT-Shipbrokers staff. Sign in with your @gtshipbrokers.com Microsoft 365 account.
          </p>

          {showDevLogin && (
            <form onSubmit={submit}>
              <div style={{ borderTop: '1px solid #e5e7eb', margin: '14px 0 12px' }} />
              <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Dev / testing fallback</div>
              <div className="form-group">
                <label>Work email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@gtshipbrokers.com" />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder="••••••••" />
              </div>
              <button type="submit" className="btn btn-secondary" style={{ width: '100%', marginTop: '4px' }}>Sign in (no SSO)</button>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }}>
                {DEMO_USERS.map(u => (
                  <button key={u.email} type="button" onClick={() => onSignIn(u)} className="btn btn-secondary btn-sm" style={{ flex: '1 1 45%' }}>
                    {u.name.split(' ')[0]}{u.role === 'admin' ? ' · admin' : ''}
                  </button>
                ))}
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── DEMO SEED DATA ───────────────────────────────────────────────────────────
const DEMO_CARRIERS = [
  { id: 1, name: 'Wecantrans sp. z o.o.', email: 'rfq@wecantrans.pl',    phone: '+48 22 123 4567',  contact_person: 'Marek Kowalski',  services: ['FTL','LTL'],        tradelanes: 'PL-EE,EE-PL,PL-LV,PL-LT,DE-PL,PL-DE' },
  { id: 2, name: 'HRX Estonia',           email: 'booking@hrx.ee',       phone: '+372 5123 4567',   contact_person: 'Andres Tamm',     services: ['FTL','LTL','EXPRESS'], tradelanes: 'EE-FI,FI-EE,EE-SE,SE-EE,EE-LV,EE-LT,EE-DE,DE-EE' },
  { id: 3, name: 'Ferroline',             email: 'rfq@ferroline.eu',     phone: '+372 6234 5678',   contact_person: 'Dmitri Ivanov',   services: ['FTL','LTL'],        tradelanes: 'EE-FI,FI-EE,EE-LV,LV-EE,EE-LT,LT-EE' },
  { id: 4, name: 'DSV Road',              email: 'dsv.rfq@dsv.com',      phone: '+45 43 200 200',   contact_person: 'Lars Nielsen',    services: ['FTL','LTL','FCL','LCL','AIR'], tradelanes: 'EE-DE,DE-EE,EE-NL,NL-EE,EE-ES,ES-EE,EE-FR,FR-EE' },
  { id: 5, name: 'ACE Logistics',         email: 'quotes@ace.ee',        phone: '+372 5456 7890',   contact_person: 'Kristjan Mägi',   services: ['FTL','LTL'],        tradelanes: 'EE-LV,LV-EE,EE-LT,LT-EE,EE-FI,FI-EE' },
  { id: 6, name: 'Alpi Eesti',            email: 'marten@alpieesti.ee',  phone: '+372 5678 9012',   contact_person: 'Marten',          services: ['FTL','OOG'],        tradelanes: 'IT-EE,EE-IT' },
  { id: 7, name: 'Arco Transport',        email: 'ivari@arcotransport.ee', phone: '+372 5234 5678', contact_person: 'Ivari',           services: ['FTL'],              tradelanes: 'IT-EE,EE-IT' },
  { id: 8, name: 'TFT Logistic',          email: 'tft@tftlogistic.eu',   phone: '+370 5 123 456',   contact_person: 'Jonas Petraitis', services: ['FTL','LTL'],        tradelanes: 'LT-PL,PL-LT,LT-DE,DE-LT,EE-LT,LT-EE' },
];

// 20-carrier showcase RFQ — 10 quoted (varied price + transit), 10 still pending.
// Demonstrates cheapest-first sorting, the receivers list, and per-/bulk-reminder.
const DEMO_SHOWCASE_RESPONDERS = [
  { name: 'Girteka Logistics',    email: 'rfq@girteka.eu',            all_in_total: 1840, transit_days: 6, notes: 'Mega trailer, tail-lift available' },
  { name: 'Vlantana',             email: 'orders@vlantana.lt',        all_in_total: 1920, transit_days: 5, notes: 'Curtainsider, loading from Mon' },
  { name: 'Hegelmann Transporte', email: 'quotes@hegelmann.com',      all_in_total: 2050, transit_days: 7, notes: 'Standard tilt, weekly departure' },
  { name: 'DSV Road',             email: 'dsv.rfq@dsv.com',           all_in_total: 2180, transit_days: 5, notes: 'Direct, no transshipment' },
  { name: 'Raben Group',          email: 'tender@raben-group.com',    all_in_total: 2240, transit_days: 8, notes: 'Groupage consolidation' },
  { name: 'DB Schenker',          email: 'landtransport@dbschenker.com', all_in_total: 2390, transit_days: 6, notes: 'Premium service, GPS tracked' },
  { name: 'Kuehne + Nagel',       email: 'road.ee@kuehne-nagel.com',  all_in_total: 2510, transit_days: 4, notes: 'Express dedicated — fastest transit' },
  { name: 'Rhenus Logistics',     email: 'road@rhenus.com',           all_in_total: 2680, transit_days: 7, notes: '' },
  { name: 'Gebrüder Weiss',       email: 'tallinn@gw-world.com',      all_in_total: 2950, transit_days: 9, notes: 'Eco route via DE hub' },
  { name: 'DHL Freight',          email: 'freight.ee@dhl.com',        all_in_total: 3200, transit_days: 6, notes: 'ADR capable, priced as quoted' },
];
const DEMO_SHOWCASE_PENDING = [
  { name: 'Dachser',            email: 'tender@dachser.com' },
  { name: "Waberer's",          email: 'sales@waberers.com' },
  { name: 'Ekol Logistics',     email: 'rfq@ekol.com' },
  { name: 'Transimeksa',        email: 'info@transimeksa.com' },
  { name: 'Itella Logistics',   email: 'freight@itella.com' },
  { name: 'cargo-partner',      email: 'quotes@cargo-partner.com' },
  { name: 'Optima Logistics',   email: 'booking@optima.ee' },
  { name: 'Northgate Transport',email: 'rfq@northgate.eu' },
  { name: 'Siccma Trans',       email: 'dispatch@siccma.lv' },
  { name: 'Baltrans',           email: 'sales@baltrans.lt' },
];

const DEMO_RFQS = [
  {
    id: 11, hp_number: 'HP-00152', origin: 'Tallinn, EE', destination: 'Barcelona, ES',
    cbm: 68, kg: 21500, status: 'responses_received',
    dims: '12 × EUR pallet, 2.2m high',
    responses: DEMO_SHOWCASE_RESPONDERS.map((c, i) => ({
      id: 9000 + i, carrier_id: null, carrier_name: c.name, carrier_email: c.email,
      all_in_total: c.all_in_total, price: c.all_in_total, currency: 'EUR',
      transit_days: c.transit_days, notes: c.notes, won: false,
      created_at: `2026-06-10T${String(10 + i).padStart(2, '0')}:30:00`,
    })),
    raw: {
      created_at: '2026-06-10T09:15:00',
      created_by: 'mairold.suuder@gtshipbrokers.com',
      draft_subject: 'HP-00152 · Tallinn → Barcelona · FTL',
      customer: 'Ensto Building Systems',
      cargo_public: 'Electrical enclosures, palletised',
      cargo_summary: 'Electrical enclosures, palletised',
      dimensions: '12 × EUR pallet, 2.2m high',
      weight_kg: 21500,
      service_type: 'FTL',
      from_country: 'EE', from_city: 'Tallinn', from_postcode: '11415',
      to_country: 'ES', to_city: 'Barcelona', to_postcode: '08040',
      ready_date: '2026-06-16',
      incoterms: 'FCA Tallinn',
      carriers_contacted: JSON.stringify([
        ...DEMO_SHOWCASE_RESPONDERS.map((c, i) => ({ name: c.name, email: c.email, message_id: `<demo-${i}@gtshipbrokers>` })),
        ...DEMO_SHOWCASE_PENDING.map((c, i) => ({ name: c.name, email: c.email, message_id: `<demo-p${i}@gtshipbrokers>` })),
      ]),
    },
  },
  {
    id: 1, hp_number: 'HP-00142', origin: 'Tallinn, EE', destination: 'Rotterdam, NL',
    cbm: 18, kg: 4200, status: 'won',
    responses: [
      { carrier_id: 1, price: 1850, currency: 'EUR', transit_days: 4, notes: 'Direct service, no transshipment', won: false },
      { carrier_id: 2, price: 1720, currency: 'EUR', transit_days: 5, notes: 'Via Riga hub', won: true },
      { carrier_id: 8, price: 1980, currency: 'EUR', transit_days: 4, notes: '', won: false },
    ],
    won_carrier_id: 2,
  },
  {
    id: 2, hp_number: 'HP-00143', origin: 'Hamburg, DE', destination: 'Tallinn, EE',
    cbm: 32, kg: 9800, status: 'open',
    responses: [
      { carrier_id: 1, price: 2400, currency: 'EUR', transit_days: 3, notes: 'FTL, available next Monday', won: false },
    ],
  },
  {
    id: 3, hp_number: 'HP-00144', origin: 'Sassuolo, IT', destination: 'Tallinn, EE',
    cbm: 45, kg: 12000, status: 'won',
    responses: [
      { carrier_id: 6, price: 3200, currency: 'EUR', transit_days: 7, notes: 'Specialist Italy-Estonia route', won: true },
      { carrier_id: 7, price: 3450, currency: 'EUR', transit_days: 6, notes: '', won: false },
      { carrier_id: 4, price: 3800, currency: 'EUR', transit_days: 8, notes: 'DSV groupage available', won: false },
    ],
    won_carrier_id: 6,
  },
  {
    id: 4, hp_number: 'HP-00145', origin: 'Rotterdam, NL', destination: 'Rakvere, EE',
    cbm: 8, kg: 1800, status: 'lost',
    responses: [
      { carrier_id: 3, price: 950, currency: 'EUR', transit_days: 5, notes: 'Groupage, weekly departure', won: false },
      { carrier_id: 5, price: 880, currency: 'EUR', transit_days: 6, notes: '', won: false },
    ],
  },
  {
    id: 5, hp_number: 'HP-00146', origin: 'Gdansk, PL', destination: 'Tallinn, EE',
    cbm: 22, kg: 6500, status: 'responses_received',
    responses: [
      { carrier_id: 1, price: 1650, currency: 'EUR', transit_days: 2, notes: 'Express, dedicated truck', won: false },
      { carrier_id: 8, price: 1480, currency: 'EUR', transit_days: 3, notes: 'Standard groupage', won: false },
    ],
  },
  {
    id: 6, hp_number: 'HP-00147', origin: 'Tallinn, EE', destination: 'Helsinki, FI',
    cbm: 5, kg: 900, status: 'open',
    responses: [],
  },
  {
    id: 7, hp_number: 'HP-00148', origin: 'Felixstowe, GB', destination: 'Tallinn, EE',
    cbm: 28, kg: 7200, status: 'no_quote',
    responses: [],
  },
  {
    id: 8, hp_number: 'HP-00149', origin: 'Tallinn, EE', destination: 'Madrid, ES',
    cbm: 14, kg: 3100, status: 'won',
    responses: [
      { carrier_id: 4, price: 2950, currency: 'EUR', transit_days: 6, notes: 'DSV direct Spain service', won: true },
      { carrier_id: 1, price: 3100, currency: 'EUR', transit_days: 7, notes: '', won: false },
    ],
    won_carrier_id: 4,
  },
  {
    id: 9, hp_number: 'HP-00150', origin: 'Riga, LV', destination: 'Frankfurt, DE',
    cbm: 36, kg: 8400, status: 'responses_received',
    responses: [
      { carrier_id: 2, price: 2200, currency: 'EUR', transit_days: 3, notes: 'FTL available this week', won: false },
      { carrier_id: 5, price: 2050, currency: 'EUR', transit_days: 4, notes: 'Good rate, need confirm', won: false },
      { carrier_id: 8, price: 2380, currency: 'EUR', transit_days: 3, notes: '', won: false },
    ],
  },
  {
    id: 10, hp_number: 'HP-00151', origin: 'Tallinn, EE', destination: 'Warsaw, PL',
    cbm: 11, kg: 2600, status: 'open',
    responses: [
      { carrier_id: 1, price: 1100, currency: 'EUR', transit_days: 2, notes: 'Quick turnaround', won: false },
    ],
  },
];

const STATUS_CONFIG = {
  open:               { label: 'Open',               color: '#1565c0', bg: '#eff6ff' },
  responses_received: { label: 'Responses In',       color: '#d97706', bg: '#fffbeb' },
  won:                { label: 'Won',                color: '#16a34a', bg: '#f0fdf4' },
  lost:               { label: 'Lost',               color: '#dc2626', bg: '#fef2f2' },
  no_quote:           { label: 'No Quote',           color: '#6b7280', bg: '#f9fafb' },
};

// ─── BACKEND SHAPE MAPPER ─────────────────────────────────────────────────────
const mapRfqFromBackend = (row) => ({
  id: row.hp_code,
  hp_number: row.hp_code,
  origin: [row.from_city, row.from_country].filter(Boolean).join(', ') || row.origin || '—',
  destination: [row.to_city, row.to_country].filter(Boolean).join(', ') || row.destination || '—',
  cbm: row.cbm ?? row.volume_cbm ?? 0,
  kg: row.kg ?? row.weight_kg ?? 0,
  status: row.status === 'AWARDED' ? 'won' : (row.status ? row.status.toLowerCase() : 'open'),
  response_count: row.response_count ?? 0,
  responses: [],
  won_carrier_id: null,
  owner: row.owner || '',
  created_by: row.created_by || '',
  raw: row,
});

// Registrable email domain, so contacts at the same company match across office
// subdomains (indrek.bauer@ee.dsv.com and eneli.ditmann@dsv.com → both "dsv.com").
const baseDomain = (email) => {
  const host = String(email || '').toLowerCase().split('@')[1] || '';
  const parts = host.split('.').filter(Boolean);
  return parts.length >= 2 ? parts.slice(-2).join('.') : host;
};
// First name from an email local part (indrek.bauer@… → "Indrek"), for the
// "you contacted X" half of a suggestion when no display name is stored.
const personFromEmail = (email) => {
  const first = (String(email || '').split('@')[0] || '').split(/[._-]/)[0] || '';
  return first ? first.charAt(0).toUpperCase() + first.slice(1) : '';
};

// The hashed JS bundle this tab loaded with. We poll index.html for a different
// hash so a long-open tab (which keeps running old JS even after a deploy — the
// no-cache header only helps on a fresh page load) can prompt the user to update
// instead of silently running stale code. Empty in dev (no hashed bundle).
const LOADED_BUNDLE = (() => {
  try {
    const m = (document.querySelector('script[src*="/static/js/main."]')?.src || '').match(/main\.[a-z0-9]+\.js/);
    return m ? m[0] : '';
  } catch { return ''; }
})();

// ─── COMPONENT ────────────────────────────────────────────────────────────────
const RFQDashboard = () => {
  // True once a newer build has been deployed than the one this tab is running.
  const [updateReady, setUpdateReady] = useState(false);
  useEffect(() => {
    if (!LOADED_BUNDLE) return; // dev / unknown — skip
    const check = async () => {
      try {
        const html = await fetch('/index.html', { cache: 'no-store' }).then(r => r.text());
        const m = html.match(/main\.[a-z0-9]+\.js/);
        if (m && m[0] !== LOADED_BUNDLE) setUpdateReady(true);
      } catch { /* offline / transient — ignore */ }
    };
    const t = setInterval(check, 60000);
    window.addEventListener('focus', check);
    return () => { clearInterval(t); window.removeEventListener('focus', check); };
  }, []);
  const [demoMode, setDemoMode]   = useState(false);
  const [rfqs, setRfqs]           = useState([]);
  const [carriers, setCarriers]   = useState([]);
  const [selectedRfq, setSelectedRfq]     = useState(null);
  const [selectedCarrier, setSelectedCarrier] = useState(null);
  const [activeTab, setActiveTab] = useState('rfqs');
  const [drawerTab, setDrawerTab] = useState('responses');
  const [quoteAddedFor, setQuoteAddedFor] = useState(''); // transient "quote added" confirmation
  const [searchTerm, setSearchTerm] = useState('');
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const routeLayer = useRef(null);

  // Modal states
  const [showCreateRFQ,   setShowCreateRFQ]   = useState(false);
  const [showCarrierModal,setShowCarrierModal] = useState(false);
  const [showResponses,   setShowResponses]   = useState(false);
  const [showEditCarrier, setShowEditCarrier] = useState(false);
  const [showOrderPreview,setShowOrderPreview] = useState(false);
  const [previewCarrier,  setPreviewCarrier]  = useState(null);
  const [previewResponse, setPreviewResponse] = useState(null);
  const [isCreatingMondayItem, setIsCreatingMondayItem] = useState(false);
  const [nudging, setNudging] = useState(false);
  // Learned lane contacts for the open RFQ's lane + which one is mid-save.
  const [laneContacts, setLaneContacts] = useState([]);
  const [savingContact, setSavingContact] = useState('');
  const [savingAll, setSavingAll] = useState(false);

  // Settings state
  // Production-ready settings: URLs from build-time env (set at `npm run build`),
  // Monday token persisted in the browser. Falls back to localhost for dev.
  const [settings, setSettings] = useState(() => {
    let saved = {};
    try { saved = JSON.parse(localStorage.getItem('rfqSettings') || '{}'); } catch { saved = {}; }
    // The add-in Node server runs over HTTPS (self-signed). Migrate any stale
    // http://localhost:3001 saved earlier so the carrier fetch isn't silently refused.
    let savedAddin = saved.addinUrl;
    if (savedAddin === 'http://localhost:3001') savedAddin = '';
    return {
      mondayToken:   saved.mondayToken   || '',
      // TEST board (5091705428) by default so a demo award never mutates the live
      // Tellimused board (1280560705). Matches the add-in's MONDAY_BOARD_ID default.
      // Flip both to 1280560705 (here + deploy/docker/.env) for real production writes.
      mondayBoardId: saved.mondayBoardId || '5091705428',
      apiUrl:   process.env.REACT_APP_API_URL   || saved.apiUrl   || 'http://localhost:8000',
      addinUrl: process.env.REACT_APP_ADDIN_URL || savedAddin || 'https://localhost:3001',
      // Shared service token for the backend + add-in /api/* guards. Empty in dev
      // (guards off). In production, paste the RFQ_SERVICE_TOKEN value here.
      serviceToken: process.env.REACT_APP_SERVICE_TOKEN || saved.serviceToken || '',
    };
  });
  // Auth on every backend/add-in call. Primary: the signed-in user's Microsoft
  // (Entra) ID token as `Authorization: Bearer …`, which the backend validates
  // per-user — so no shared secret needs to live in this public bundle. The legacy
  // X-RFQ-Token is still sent if one happens to be configured (dev / transition).
  const authedFetch = async (url, opts = {}, _retry = false) => {
    const headers = { ...(opts.headers || {}) };
    if (settings.serviceToken) headers['X-RFQ-Token'] = settings.serviceToken;
    let sentBearer = false;
    if (msalInstance) {
      try {
        await ensureMsal();
        const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
        if (account) {
          // forceRefresh on a retry: acquireTokenSilent can return a STALE/expired
          // idToken (it renews the access token without always re-issuing the id
          // token), which the backend rejects with 401 → empty dashboard. A forced
          // refresh fetches a fresh one. This was why per-user SSO silently failed.
          const res = await msalInstance.acquireTokenSilent({ scopes: ['User.Read'], account, forceRefresh: _retry });
          if (res?.idToken) { headers['Authorization'] = `Bearer ${res.idToken}`; sentBearer = true; }
        }
      } catch {
        // Silent acquisition failed (consent / expired refresh token / no cached
        // account). Re-authenticate interactively so the user isn't stuck on empty
        // data — once only, not on the retry pass.
        if (!_retry && msalInstance.getActiveAccount()) {
          try { await msalInstance.acquireTokenRedirect({ scopes: ['User.Read'] }); } catch { /* ignore */ }
        }
      }
    }
    const resp = await fetch(url, { ...opts, headers });
    // A 401 on a bearer-authed call almost always means the idToken was stale —
    // refresh once and retry before surfacing an empty list.
    if (resp.status === 401 && sentBearer && !_retry) {
      return authedFetch(url, opts, true);
    }
    return resp;
  };
  useEffect(() => {
    try { localStorage.setItem('rfqSettings', JSON.stringify(settings)); } catch { /* ignore */ }
  }, [settings]);

  // Rich carriers (from add-in Node server)
  const [richCarriers, setRichCarriers] = useState([]);
  const [editingCarrierEmail, setEditingCarrierEmail] = useState(null);
  const [bouncedOnly, setBouncedOnly] = useState(false);
  const [carrierFetchError, setCarrierFetchError] = useState('');
  const [carrierServiceFilter, setCarrierServiceFilter] = useState('');
  const [carrierHqFilter, setCarrierHqFilter] = useState('');
  const [carrierLaneFilter, setCarrierLaneFilter] = useState('');
  const [carrierSort, setCarrierSort] = useState({ key: 'company_name', dir: 'asc' });

  const fetchRichCarriers = () => {
    authedFetch(`${settings.addinUrl}/api/carriers/all`)
      .then(r => r.json())
      .then(d => { setRichCarriers(d.carriers || []); setCarrierFetchError(''); })
      .catch(err => {
        setCarrierFetchError(
          `Could not reach the carrier server at ${settings.addinUrl}. ` +
          `It runs over HTTPS with a self-signed certificate — open ` +
          `${settings.addinUrl}/api/carriers/all in a new tab once and accept the warning, ` +
          `then reload. (${err.message})`
        );
      });
  };

  // Lane scoreboard (Scoreboard tab): the add-in's true ranked order for one
  // service + lane, fetched from /api/carriers/scoreboard.
  const [sbService, setSbService] = useState('FTL');
  const [sbFrom, setSbFrom] = useState('');
  const [sbTo, setSbTo] = useState('');
  const [sbRows, setSbRows] = useState([]);
  const [sbLoading, setSbLoading] = useState(false);
  const [sbError, setSbError] = useState('');
  const [sbPointsDraft, setSbPointsDraft] = useState({}); // email -> manual-points input value
  const [sbAddSearch, setSbAddSearch] = useState('');
  const [sbAddResults, setSbAddResults] = useState([]);
  const [sbBusy, setSbBusy] = useState(''); // email currently being saved
  const sbLane = `${sbFrom.trim().toUpperCase()}-${sbTo.trim().toUpperCase()}`;
  const sbReady = Boolean(sbService) && /^[A-Z]{2}$/.test(sbFrom.trim().toUpperCase()) && /^[A-Z]{2}$/.test(sbTo.trim().toUpperCase());

  const fetchScoreboard = () => {
    if (!sbReady) { setSbRows([]); return; }
    setSbLoading(true); setSbError('');
    authedFetch(`${settings.addinUrl}/api/carriers/scoreboard?service=${encodeURIComponent(sbService)}&from=${encodeURIComponent(sbFrom.trim())}&to=${encodeURIComponent(sbTo.trim())}&limit=300`)
      .then(r => r.json())
      .then(d => { setSbRows(d.carriers || []); setSbPointsDraft({}); })
      .catch(err => setSbError(`Could not load the scoreboard: ${err.message}`))
      .finally(() => setSbLoading(false));
  };
  useEffect(() => {
    if (activeTab !== 'scoreboard') return;
    fetchScoreboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, sbService, sbFrom, sbTo]);

  // Quick scoreboard edits reuse the same override upsert as the carrier editor.
  // Identity fields ride along so a points-only save can't blank the contact
  // (upsertCarrier writes contact_name verbatim, "" included).
  const sbSave = async (row, patch) => {
    if (demoMode) return;
    setSbBusy(row.email);
    try {
      const res = await authedFetch(`${settings.addinUrl}/api/carriers/override`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: row.email, company_name: row.company_name || '', contact_name: row.contact_name || '', ...patch }),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => null);
        window.alert(`Change not saved: ${detail?.error || res.statusText || 'unknown error'}`);
      }
    } catch (err) {
      window.alert(`Change not saved: ${err.message || 'network error'}`);
    }
    setSbBusy('');
    fetchScoreboard();
    fetchRichCarriers();
  };

  // "Add carrier to this lane" search box — full-DB search, scoped to the query.
  const sbSearchDb = async (q) => {
    setSbAddSearch(q);
    if (q.trim().length < 2) { setSbAddResults([]); return; }
    try {
      const r = await authedFetch(`${settings.addinUrl}/api/carriers/search?q=${encodeURIComponent(q)}&service=${encodeURIComponent(sbService)}&from=${encodeURIComponent(sbFrom.trim())}&to=${encodeURIComponent(sbTo.trim())}&limit=8`);
      const d = await r.json();
      setSbAddResults(d.carriers || []);
    } catch {
      setSbAddResults([]);
    }
  };

  // Form states
  const [rfqForm, setRfqForm] = useState({ hp_number: '', origin: '', destination: '', dims: '', kg: 0 });
  const [carrierForm, setCarrierForm] = useState({ name: '', email: '', phone: '', contact_person: '', services: [], tradelanes: '' });
  const [responseForm, setResponseForm] = useState({ carrier_name: '', carrier_email: '', price: 0, currency: 'EUR', transit_days: 0, notes: '' });
  const [quoteCarrier, setQuoteCarrier] = useState('');
  const [laneInput, setLaneInput] = useState('');
  // Clear the Add-Quote form whenever a different RFQ is opened, so a half-typed
  // quote can never carry over and get attached to the wrong RFQ.
  useEffect(() => {
    setResponseForm({ carrier_name: '', carrier_email: '', price: 0, currency: 'EUR', transit_days: 0, notes: '' });
    setQuoteCarrier('');
  }, [selectedRfq?.id]);

  // Load the learned lane contacts for the open RFQ's lane (so a suggestion that
  // was already confirmed shows as saved rather than offering to save again).
  useEffect(() => {
    const frm = (selectedRfq?.raw?.from_country || '').toUpperCase();
    const to = (selectedRfq?.raw?.to_country || '').toUpperCase();
    if (demoMode || !selectedRfq || !frm || !to) { setLaneContacts([]); return; }
    authedFetch(`${settings.apiUrl}/api/lane-contacts?from_country=${frm}&to_country=${to}`)
      .then(r => r.json()).then(d => setLaneContacts(d.lane_contacts || [])).catch(() => setLaneContacts([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRfq?.id]);

  // Confirm "this replier is the right person for this lane" → persist it.
  const confirmLaneContact = async ({ email, name, company }) => {
    const frm = (selectedRfq?.raw?.from_country || '').toUpperCase();
    const to = (selectedRfq?.raw?.to_country || '').toUpperCase();
    const company_domain = baseDomain(email);
    if (!email || !frm || !to || !company_domain) return;
    if (demoMode) { setLaneContacts(prev => [...prev, { company_domain, from_country: frm, to_country: to, email, contact_name: name }]); return; }
    setSavingContact(email);
    try {
      const res = await authedFetch(`${settings.apiUrl}/api/lane-contacts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_domain, from_country: frm, to_country: to,
          email, contact_name: name || '', company_name: company || '',
          source_hp: selectedRfq.hp_number, confirmed_by: authUser?.email || '',
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const d = await res.json();
      setLaneContacts(prev => [
        ...prev.filter(x => x.company_domain !== company_domain),
        d.lane_contact,
      ]);
    } catch (err) {
      alert('Could not save contact: ' + err.message);
    } finally {
      setSavingContact('');
    }
  };

  // Confirm several discovered contacts at once ("Confirm all" on the banner).
  const confirmLaneContacts = async (items) => {
    const frm = (selectedRfq?.raw?.from_country || '').toUpperCase();
    const to = (selectedRfq?.raw?.to_country || '').toUpperCase();
    if (!items.length || !frm || !to) return;
    if (demoMode) {
      setLaneContacts(prev => [
        ...prev,
        ...items.map(it => ({ company_domain: baseDomain(it.email), from_country: frm, to_country: to, email: it.email, contact_name: it.name })),
      ]);
      return;
    }
    setSavingAll(true);
    try {
      const results = await Promise.all(items.map(it =>
        authedFetch(`${settings.apiUrl}/api/lane-contacts`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            company_domain: baseDomain(it.email), from_country: frm, to_country: to,
            email: it.email, contact_name: it.name || '', company_name: it.company || '',
            source_hp: selectedRfq.hp_number, confirmed_by: authUser?.email || '',
          }),
        }).then(r => r.ok ? r.json() : null).catch(() => null)
      ));
      const saved = results.filter(Boolean).map(d => d.lane_contact);
      if (saved.length) {
        const domains = new Set(saved.map(s => s.company_domain));
        setLaneContacts(prev => [...prev.filter(x => !domains.has(x.company_domain)), ...saved]);
      }
    } finally {
      setSavingAll(false);
    }
  };
  const [vetSvc, setVetSvc] = useState('*');
  const [vetLane, setVetLane] = useState('');
  // Auth / identity (RFQ ownership + My/All manager scope). Persisted locally.
  const [authUser, setAuthUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('rfqAuthUser') || 'null'); } catch { return null; }
  });
  const [scope, setScope] = useState(() => {
    try { const u = JSON.parse(localStorage.getItem('rfqAuthUser') || 'null'); return u && u.role === 'admin' ? 'all' : 'mine'; }
    catch { return 'all'; }
  });
  const signIn = (u) => {
    setAuthUser(u);
    setScope(u && u.role === 'admin' ? 'all' : 'mine');
    try { localStorage.setItem('rfqAuthUser', JSON.stringify(u)); } catch { /* ignore */ }
  };
  const signOut = () => {
    setAuthUser(null);
    try { localStorage.removeItem('rfqAuthUser'); } catch { /* ignore */ }
  };

  // ── Microsoft 365 SSO: handle the redirect coming back from login.microsoftonline.com.
  // We use redirect (not popup) sign-in — popups break when the dashboard tab itself
  // has a window.opener (MSAL "block_nested_popups"). Start "busy" only when the URL
  // carries an auth response so a normal load never shows a spinner.
  const [ssoBusy, setSsoBusy] = useState(
    () => msalConfigured && typeof window !== 'undefined' && /[#&](code|error|state)=/.test(window.location.hash)
  );
  useEffect(() => {
    if (!msalConfigured) return;
    let cancelled = false;
    ensureMsal()
      .then(() => msalInstance.handleRedirectPromise())
      .then(res => {
        if (cancelled) return;
        const account = res?.account || msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
        // Pin the active account so acquireTokenSilent works on later reloads, not
        // just immediately after the redirect.
        if (account) msalInstance.setActiveAccount(account);
        if (res && res.account) {
          const em = (res.account.username || '').toLowerCase();
          const dom = em.split('@')[1] || '';
          if (ALLOWED_EMAIL_DOMAINS.includes(dom)) {
            signIn(resolveUser(em, res.account.name));
          }
        }
        setSsoBusy(false);
      })
      .catch(() => { if (!cancelled) setSsoBusy(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isAdmin = authUser?.role === 'admin';
  const myEmail = (authUser?.email || '').toLowerCase();
  const isMine = (rfq) => {
    const owner = (rfq.owner || '').toLowerCase();
    const createdBy = (rfq.created_by || '').toLowerCase();
    return (!!owner && owner === myEmail) || (!!createdBy && createdBy === myEmail);
  };
  const [orderForm, setOrderForm] = useState({
    itemName: '', orderDate: '', status: 'New shipment', clientRef: '',
    pickupCompany: '', pickupAddress: '', pickupCountry: '', pickupContact: '', pickupRef: '', pickupDate: '',
    deliveryTo: '', deliveryAddress: '', deliveryCountry: '', deliveryContact: '', deliveryRef: '', deliveryDate: '',
    goodsDesc: '', cargoDimensions: '', ldm: '', weightKg: '',
    comments: '', trpMode: 'ROAD FREIGHT', carrierInstructions: '',
    carrier: '', carrierEmail: '', truckNr: '', costEur: '', rfqNo: '',
  });

  // ── Rich carriers fetch (from add-in Node server) ──
  useEffect(() => {
    if (demoMode) {
      setRichCarriers(DEMO_CARRIERS.map((c, i) => ({
        company_name: c.name,
        email: c.email,
        contact_name: c.contact_person,
        services: c.services,
        lanes: (c.tradelanes || '').split(',').map(t => t.trim()).filter(Boolean),
        hq_country_code: '',
        source: 'demo',
        points: [360, 110, 10, 210, 10, 110, 10, 10, 60, 10][i % 10],
        won_count: [3, 1, 0, 2, 0, 1, 0, 0, 0, 0][i % 10],
      })));
      return;
    }
    fetchRichCarriers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demoMode, settings.addinUrl]);

  // ── Live API fetch (skipped in demo mode) ──
  useEffect(() => {
    if (demoMode) {
      // Distribute demo RFQs across the demo users so My/All scope is visible.
      setRfqs(DEMO_RFQS.map((r, i) => {
        const u = DEMO_USERS[i % DEMO_USERS.length];
        return { ...r, owner: u.email, created_by: u.email };
      }));
      setCarriers(DEMO_CARRIERS);
      return;
    }
    const fetchAll = () => {
      authedFetch(`${settings.apiUrl}/api/rfqs`)
        .then(r => r.json()).then(d => setRfqs((d.rfqs || []).map(mapRfqFromBackend))).catch(() => {});
      authedFetch(`${settings.apiUrl}/api/carriers`)
        .then(r => r.json()).then(d => setCarriers(d.carriers || [])).catch(() => {});
    };
    fetchAll();
    const t = setInterval(fetchAll, 15000);
    return () => clearInterval(t);
  // authUser in deps → re-fetch right after sign-in, when the MS token exists.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demoMode, settings.apiUrl, authUser]);

  // Map init
  useEffect(() => {
    if (showCreateRFQ && mapRef.current && !mapInstance.current) {
      setTimeout(() => {
        mapInstance.current = L.map(mapRef.current).setView([54, 18], 4);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors', maxZoom: 19
        }).addTo(mapInstance.current);
        mapInstance.current.invalidateSize();
      }, 100);
    }
    if (!showCreateRFQ && mapInstance.current) {
      mapInstance.current.remove();
      mapInstance.current = null;
      routeLayer.current = null;
    }
  }, [showCreateRFQ]);

  // Draw the origin → destination route (markers + line) whenever they change.
  useEffect(() => {
    if (!showCreateRFQ) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      if (!mapInstance.current) return;
      const [o, d] = await Promise.all([geocodeCity(rfqForm.origin), geocodeCity(rfqForm.destination)]);
      if (cancelled || !mapInstance.current) return;
      if (routeLayer.current) { routeLayer.current.remove(); routeLayer.current = null; }
      const group = L.layerGroup().addTo(mapInstance.current);
      routeLayer.current = group;
      mapInstance.current.invalidateSize();
      const pts = [];
      if (o) {
        L.circleMarker(o, { radius: 7, color: '#fff', weight: 2, fillColor: '#16a34a', fillOpacity: 1 })
          .addTo(group).bindTooltip('Origin: ' + (rfqForm.origin.split(',')[0] || ''), { permanent: false });
        pts.push(o);
      }
      if (d) {
        L.circleMarker(d, { radius: 7, color: '#fff', weight: 2, fillColor: '#1565c0', fillOpacity: 1 })
          .addTo(group).bindTooltip('Destination: ' + (rfqForm.destination.split(',')[0] || ''), { permanent: false });
        pts.push(d);
      }
      if (o && d) L.polyline([o, d], { color: '#1565c0', weight: 3, dashArray: '6 7', opacity: 0.85 }).addTo(group);
      if (pts.length === 2) mapInstance.current.fitBounds(L.latLngBounds(pts).pad(0.35));
      else if (pts.length === 1) mapInstance.current.setView(pts[0], 6);
    }, 450); // debounce typing
    return () => { cancelled = true; clearTimeout(t); };
  }, [showCreateRFQ, rfqForm.origin, rfqForm.destination]);

  // ── RFQ handlers ──
  const handleCreateRfq = async () => {
    if (!rfqForm.hp_number || !rfqForm.origin || !rfqForm.destination) return;
    const newRfq = {
      id: Date.now(), ...rfqForm, status: 'open', responses: [],
      owner: myEmail, created_by: myEmail,
    };
    if (demoMode) {
      setRfqs(prev => [newRfq, ...prev]);
      setShowCreateRFQ(false);
      setRfqForm({ hp_number: '', origin: '', destination: '', dims: '', kg: 0 });
      return;
    }
    try {
      const res = await authedFetch(`${settings.apiUrl}/api/rfq/create`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...rfqForm, owner: myEmail }),
      });
      const data = await res.json();
      if (data.success) {
        setRfqs(prev => [newRfq, ...prev]);
        setShowCreateRFQ(false);
        setRfqForm({ hp_number: '', origin: '', destination: '', dims: '', kg: 0 });
      }
    } catch { setRfqs(prev => [newRfq, ...prev]); setShowCreateRFQ(false); }
  };

  const mapRespFromRaw = (r) => ({
    id: r.id,
    carrier_id: r.carrier_id,
    carrier_email: r.carrier_email || '',
    carrier_name: r.carrier_name || r.carrier_email || (r.carrier_id ? `Carrier ${r.carrier_id}` : 'Unknown'),
    price: r.price,
    currency: r.currency || 'EUR',
    transit_days: r.transit_days,
    notes: r.notes,
    rate_basis: r.rate_basis,
    unit_rate: r.unit_rate,
    chargeable_weight: r.chargeable_weight,
    validity_date: r.validity_date,
    charges_json: r.charges_json,
    all_in_total: r.all_in_total,
    confidence: r.confidence,
    won: false,
  });

  const handleAddResponse = async () => {
    if (!selectedRfq || !responseForm.carrier_name) return;
    const cname = responseForm.carrier_name;
    const resetForm = () => {
      setResponseForm({ carrier_name: '', carrier_email: '', price: 0, currency: 'EUR', transit_days: 0, notes: '' });
      setQuoteCarrier('');
    };
    // After a successful add: clear the form, jump to the Responses tab so the new
    // quote is visibly in the list, and flash a confirmation that auto-clears.
    const confirmAdded = () => {
      resetForm();
      setDrawerTab('responses');
      setQuoteAddedFor(cname);
      setTimeout(() => setQuoteAddedFor(''), 4000);
    };
    if (demoMode) {
      const newResp = {
        id: Date.now(),
        carrier_id: null,
        carrier_email: responseForm.carrier_email,
        carrier_name: responseForm.carrier_name,
        price: Number(responseForm.price),
        currency: responseForm.currency,
        transit_days: Number(responseForm.transit_days),
        notes: responseForm.notes,
        rate_basis: null, unit_rate: null, chargeable_weight: null,
        validity_date: null, charges_json: null, all_in_total: null, confidence: null,
        won: false,
      };
      setRfqs(prev => prev.map(r => r.id === selectedRfq.id
        ? { ...r, responses: [...(r.responses || []), newResp], status: 'responses_received' }
        : r
      ));
      setSelectedRfq(prev => ({ ...prev, responses: [...(prev.responses || []), newResp], status: 'responses_received' }));
      confirmAdded();
      return;
    }
    try {
      await authedFetch(`${settings.apiUrl}/api/rfq/${selectedRfq.hp_number}/response`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          carrier_name: responseForm.carrier_name,
          carrier_email: responseForm.carrier_email,
          price: Number(responseForm.price),
          currency: responseForm.currency,
          transit_days: Number(responseForm.transit_days),
          notes: responseForm.notes,
        }),
      });
      const r = await authedFetch(`${settings.apiUrl}/api/rfq/${selectedRfq.hp_number}/responses`);
      const d = await r.json();
      const mapped = (d.responses || []).map(mapRespFromRaw);
      setSelectedRfq(prev => ({ ...prev, responses: mapped }));
      confirmAdded();
    } catch (err) {
      alert('Failed to save quote: ' + err.message);
    }
  };

  const handleMarkCarrierAsWon = async () => {
    if (!selectedRfq) return;
    setIsCreatingMondayItem(true);
    const carrier = previewCarrier;

    // Update local RFQ state
    setRfqs(prev => prev.map(r => r.id === selectedRfq.id
      ? {
          ...r, status: 'won', won_carrier_id: carrier?.id,
          responses: (r.responses || []).map(rs =>
            rs.carrier_id === carrier?.id ? { ...rs, won: true } : { ...rs, won: false }
          ),
        }
      : r
    ));

    if (demoMode) {
      setTimeout(() => {
        alert(`✓ Demo: New item would be created in Monday.com Tellimused\n\nItem: ${orderForm.itemName}\nCarrier: ${orderForm.carrier}\nEmail: ${orderForm.carrierEmail}\nCost: ${orderForm.costEur} EUR\nRoute: ${orderForm.pickupCompany} → ${orderForm.deliveryTo}`);
        setIsCreatingMondayItem(false);
        setShowOrderPreview(false);
        setShowResponses(false);
      }, 600);
      return;
    }

    try {
      const colValsObj = {};
      if (orderForm.orderDate) colValsObj.date3 = { date: orderForm.orderDate };
      if (orderForm.clientRef)  colValsObj.text = orderForm.clientRef;
      if (orderForm.status)     colValsObj.status1 = { label: orderForm.status };
      if (orderForm.pickupCompany)   colValsObj.text3 = orderForm.pickupCompany;
      if (orderForm.pickupAddress)   colValsObj.pick_up_address__1 = { text: orderForm.pickupAddress };
      if (orderForm.pickupCountry) {
        const pc = COUNTRIES.find(c => c.code === orderForm.pickupCountry.toUpperCase());
        colValsObj.country6 = { countryCode: orderForm.pickupCountry.toUpperCase(), countryName: pc?.name || orderForm.pickupCountry };
      }
      if (orderForm.pickupContact)   colValsObj.text471 = orderForm.pickupContact;
      if (orderForm.pickupRef)       colValsObj.text65  = orderForm.pickupRef;
      if (orderForm.pickupDate)      colValsObj.date    = { date: orderForm.pickupDate };
      if (orderForm.deliveryTo)      colValsObj.text94  = orderForm.deliveryTo;
      if (orderForm.deliveryAddress) colValsObj.delivery_address__1 = { text: orderForm.deliveryAddress };
      if (orderForm.deliveryCountry) {
        const dc = COUNTRIES.find(c => c.code === orderForm.deliveryCountry.toUpperCase());
        colValsObj.country_1 = { countryCode: orderForm.deliveryCountry.toUpperCase(), countryName: dc?.name || orderForm.deliveryCountry };
      }
      if (orderForm.deliveryContact) colValsObj.text51  = orderForm.deliveryContact;
      if (orderForm.deliveryRef)     colValsObj.text17  = orderForm.deliveryRef;
      if (orderForm.deliveryDate)    colValsObj.date7   = { date: orderForm.deliveryDate };
      if (orderForm.goodsDesc)       colValsObj.text6   = orderForm.goodsDesc;
      if (orderForm.cargoDimensions) colValsObj.text35  = orderForm.cargoDimensions;
      if (orderForm.ldm)             colValsObj.numbers2 = Number(orderForm.ldm);
      if (orderForm.weightKg)        colValsObj.numbers  = Number(orderForm.weightKg);
      if (orderForm.comments)        colValsObj.comments = { text: orderForm.comments };
      if (orderForm.trpMode)         colValsObj.trp_mode = { labels: [orderForm.trpMode] };
      if (orderForm.carrierInstructions) colValsObj.text54 = orderForm.carrierInstructions;
      if (orderForm.carrier)         colValsObj.text85 = orderForm.carrier;
      if (orderForm.carrierEmail)    colValsObj.text_mm3hfd1d = orderForm.carrierEmail;
      if (orderForm.truckNr)         colValsObj.text92 = orderForm.truckNr;
      if (orderForm.costEur)         colValsObj.numbers6 = Number(orderForm.costEur);
      if (orderForm.rfqNo)           colValsObj.text98 = orderForm.rfqNo;

      // Route through the add-in's server-side Monday endpoint so the Monday API
      // token lives only on the server (.env MONDAY_API_TOKEN) — never in a
      // manager's browser. The server also picks the board (.env MONDAY_BOARD_ID,
      // TEST by default), so we deliberately don't send board_id from here.
      const res = await authedFetch(`${settings.addinUrl}/api/monday/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_name: orderForm.itemName, column_values: colValsObj }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.id) { alert(`Monday.com error: ${data.error || ('HTTP ' + res.status)}`); }
      else {
        // Monday order created → record the award in our DB (status AWARDED +
        // the Monday item id, with HP-XXXX as the cross-reference).
        const mondayItemId = data.id;
        if (selectedRfq?.hp_number && previewResponse?.id != null) {
          try {
            await authedFetch(`${settings.apiUrl}/api/rfq/${selectedRfq.hp_number}/award`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ response_id: previewResponse.id, monday_item_id: mondayItemId }),
            });
          } catch { /* Monday item is created; DB award sync can be retried */ }
        }
        // Credit carrier points ONLY now — the winning carrier actually got the
        // order (Monday item created). This is the single place usage points are
        // earned; sending an RFQ no longer credits anyone.
        const wonEmail = orderForm.carrierEmail || previewCarrier?.email;
        const wonFrom  = (orderForm.pickupCountry || selectedRfq?.from_country || '').toUpperCase();
        const wonTo    = (orderForm.deliveryCountry || selectedRfq?.to_country || '').toUpperCase();
        const wonSvc   = selectedRfq?.service_type || orderForm.trpMode || '';
        if (wonEmail && wonFrom && wonTo && wonSvc) {
          try {
            await authedFetch(`${settings.addinUrl}/api/carrier-usage`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                rfq: { service_type: wonSvc, from_country: wonFrom, to_country: wonTo },
                carriers: [{ email: wonEmail, company_name: orderForm.carrier || previewCarrier?.name || '' }],
                won: true,
              }),
            });
          } catch { /* points credit is best-effort; order is already placed */ }
        }
        alert(`✓ Created in Monday.com Tellimused!${data.is_test ? ' (TEST board)' : ''}\nItem: ${orderForm.itemName}\nCarrier: ${orderForm.carrier}${data.gt_number ? `\nGT no: ${data.gt_number}` : ''}`);
      }
    } catch (err) { alert(`Error: ${err.message}`); }
    finally { setIsCreatingMondayItem(false); setShowOrderPreview(false); setShowResponses(false); }
  };

  // ── Carrier handlers (rich store — add-in Node server) ──
  const parseLanes = (tradelanes) =>
    (tradelanes || '')
      .split(/[,;\s]+/)
      .map(t => t.trim().toUpperCase())
      .filter(t => /^[A-Z]{2}-([A-Z]{2}|\*)$/.test(t));

  // Tradelane shorthand → concrete CC-CC / CC-* lanes the add-in matcher understands.
  //   EE<>DE,NL  → EE-DE, DE-EE, EE-NL, NL-EE   (both ways)
  //   EE>FR,DE   → EE-FR, EE-DE                 (outbound)
  //   EE<FR      → FR-EE                        (inbound)
  //   EE>*  /  EE<*  → EE-*  (whole EE lane; matcher treats CC-* as bidirectional)
  //   EE-DE      → EE-DE                        (explicit, passes through)
  // Origin-wildcard ("*-EE") is intentionally NOT emitted — the matcher only
  // checks `FROM-TO`, `FROM-*` and `TO-*`, so `EE-*` already covers EE imports too.
  const expandLanes = (str) => {
    const out = [], seen = new Set();
    const push = k => { if (!seen.has(k)) { seen.add(k); out.push(k); } };
    const ok = x => /^[A-Z]{2}$/.test(x);
    const addPair = (a, b) => {
      a = (a || '').trim().toUpperCase(); b = (b || '').trim().toUpperCase();
      if (b === '*') { if (ok(a)) push(a + '-*'); return; }
      if (ok(a) && ok(b) && a !== b) push(a + '-' + b);
    };
    (str || '').split(/[;\n]+/).forEach(seg => {
      seg = seg.trim(); if (!seg) return;
      const m = seg.match(/^([A-Za-z]{2})\s*(<=>|<>|>|<)\s*(.+)$/);
      if (m) {
        const base = m[1].toUpperCase(), op = m[2];
        m[3].split(/[,\s]+/).map(s => s.trim()).filter(Boolean).forEach(t => {
          if (t === '*') { push(base + '-*'); return; }   // EE>* and EE<* → EE-*
          if (op === '>' || op === '<>' || op === '<=>') addPair(base, t);
          if (op === '<' || op === '<>' || op === '<=>') addPair(t, base);
        });
      } else {
        seg.split(/[,\s]+/).map(s => s.trim()).filter(Boolean).forEach(p => {
          const lm = p.match(/^([A-Za-z]{2})-([A-Za-z]{2}|\*)$/);
          if (lm) addPair(lm[1], lm[2]);
        });
      }
    });
    return out;
  };

  // Single-cell lanes editor with shorthand expansion + removable chips.
  // Source of truth stays `carrierForm.tradelanes` (comma string) so the existing
  // create/update handlers (which call parseLanes) are unchanged.
  const renderLanesEditor = () => {
    const chips = parseLanes(carrierForm.tradelanes);
    const addLanesFromInput = () => {
      const toAdd = expandLanes(laneInput);
      if (!toAdd.length) { alert('Could not read a lane. Try  EE-DE  ·  EE<>DE,NL  ·  EE>FR  ·  EE>*'); return; }
      const merged = Array.from(new Set([...chips, ...toAdd]));
      setCarrierForm(f => ({ ...f, tradelanes: merged.join(', ') }));
      setLaneInput('');
    };
    const removeLane = (lane) =>
      setCarrierForm(f => ({ ...f, tradelanes: parseLanes(f.tradelanes).filter(l => l !== lane).join(', ') }));
    return (
      <div className="form-group">
        <label>Lanes served</label>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input type="text" value={laneInput}
            onChange={e => setLaneInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addLanesFromInput(); } }}
            placeholder="EE<>DE,NL,BE   ·   EE>FR   ·   EE>*   ·   EE-DE"
            style={{ flex: 1 }} />
          <button type="button" onClick={addLanesFromInput} className="btn btn-secondary" style={{ flexShrink: 0 }}>Add</button>
        </div>
        <p style={{ margin: '5px 0 0 0', fontSize: '11px', color: '#9ca3af' }}>
          <strong>EE&lt;&gt;DE,NL</strong> both ways · <strong>EE&gt;FR</strong> outbound · <strong>EE&lt;FR</strong> inbound · <strong>EE&gt;*</strong> whole EE lane · plain <strong>EE-DE</strong> too.
        </p>
        {chips.length > 0 && (
          <div className="lane-chips">
            {chips.map(l => (
              <span key={l} className="lane-chip">{l}<button type="button" onClick={() => removeLane(l)} title="Remove lane">✕</button></span>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ★ Vetted (service + lane auto-select) editor — ADDITIVE to the points model.
  // Vetted carriers float to the top of the Outlook add-in match list and are
  // auto-selected for that service+lane; points carriers rank after them.
  const renderVettingEditor = () => {
    const rules = carrierForm.vetted || [];
    const addRule = () => {
      const svc = vetSvc || '*';
      const raw = (vetLane || '').trim().toUpperCase();
      let lane;
      if (!raw || raw === '*' || raw === 'ALL') lane = '*';
      else lane = expandLanes(raw)[0] || (/^[A-Z]{2}-([A-Z]{2}|\*)$/.test(raw) ? raw : '');
      if (!lane) { alert('Enter a lane like  EE-DE  ·  EE>*  ·  or  *  for all lanes.'); return; }
      if (rules.some(r => r.service === svc && r.lane === lane)) { setVetLane(''); return; }
      setCarrierForm(f => ({ ...f, vetted: [...(f.vetted || []), { service: svc, lane }] }));
      setVetLane('');
    };
    const removeRule = (i) => setCarrierForm(f => ({ ...f, vetted: (f.vetted || []).filter((_, idx) => idx !== i) }));
    const fmt = (r) => `${r.service === '*' ? 'All svc' : r.service} · ${r.lane === '*' ? 'All lanes' : r.lane}`;
    return (
      <div className="form-group" style={{ background: '#eef6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '12px', marginTop: '4px' }}>
        <label style={{ fontWeight: 800, color: '#0F2746' }}>★ Vetted — auto-select (service + lane)</label>
        <p style={{ margin: '2px 0 10px 0', fontSize: '11px', color: '#64748b', lineHeight: 1.5 }}>
          A vetted carrier is floated to the <strong>top</strong> of the Outlook match list and auto-selected for that service + lane (points carriers rank after). Use <strong>All services</strong> / <strong>*</strong> for broad, or be specific like <strong>AIR · EE-DE</strong> — vetting for AIR does <strong>not</strong> elevate them on sea.
        </p>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
          <select value={vetSvc} onChange={e => setVetSvc(e.target.value)} style={{ flex: '0 0 140px' }}>
            <option value="*">All services</option>
            {ALL_SERVICES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <input type="text" value={vetLane}
            onChange={e => setVetLane(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addRule(); } }}
            placeholder="EE-DE   ·   EE>*   ·   *  (all lanes)"
            style={{ flex: 1 }} />
          <button type="button" onClick={addRule} className="btn btn-primary" style={{ flexShrink: 0 }}>Vet</button>
        </div>
        {rules.length > 0 && (
          <div className="lane-chips">
            {rules.map((r, i) => (
              <span key={i} className="lane-chip" style={{ background: '#0F2746', color: '#fff', borderColor: '#0F2746' }}>
                ★ {fmt(r)}<button type="button" onClick={() => removeRule(i)} title="Remove vetting" style={{ color: '#bfdbfe' }}>✕</button>
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  const handleCreateCarrier = async () => {
    if (!carrierForm.name || !carrierForm.email) return;
    if (demoMode) {
      const newCarrier = {
        company_name: carrierForm.name, email: carrierForm.email,
        contact_name: carrierForm.contact_person,
        services: carrierForm.services,
        lanes: parseLanes(carrierForm.tradelanes),
        vetted: carrierForm.vetted || [],
        hq_country_code: carrierForm.hq_country_code || '',
        source: 'manual',
      };
      setRichCarriers(prev => [...prev, newCarrier]);
      setShowCarrierModal(false);
      setCarrierForm({ name: '', email: '', phone: '', contact_person: '', services: [], tradelanes: '' });
      return;
    }
    try {
      const res = await authedFetch(`${settings.addinUrl}/api/carriers/override`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: carrierForm.email,
          company_name: carrierForm.name,
          contact_name: carrierForm.contact_person,
          services: carrierForm.services,
          lanes: parseLanes(carrierForm.tradelanes),
          vetted: carrierForm.vetted || [],
          // Set when the Scoreboard "+ New carrier" flow preseeds the lane, so a
          // carrier added from the board lands on it as preferred (+250).
          ...(carrierForm.preferred_lanes ? { preferred_lanes: parseLanes(carrierForm.preferred_lanes) } : {}),
          ...(carrierForm.hq_country_code ? { hq_country_code: carrierForm.hq_country_code } : {}),
        }),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => null);
        window.alert(`Carrier could not be saved: ${detail?.error || res.statusText || 'unknown error'}`);
        return;
      }
    } catch (err) {
      window.alert(`Carrier could not be saved: ${err.message || 'network error'}`);
      return;
    }
    setShowCarrierModal(false);
    setCarrierForm({ name: '', email: '', phone: '', contact_person: '', services: [], tradelanes: '' });
    fetchRichCarriers();
    fetchScoreboard();
  };

  const handleUpdateCarrier = async () => {
    if (!selectedCarrier) return;
    const newEmail = carrierForm.email;
    const body = {
      email: newEmail,
      company_name: carrierForm.name,
      contact_name: carrierForm.contact_person,
      services: carrierForm.services,
      lanes: parseLanes(carrierForm.tradelanes),
      manual_points: Number(carrierForm.manual_points) || 0,
      preferred_lanes: parseLanes(carrierForm.preferred_lanes || ''),
      vetted: carrierForm.vetted || [],
      ...(carrierForm.hq_country_code ? { hq_country_code: carrierForm.hq_country_code } : {}),
    };
    if (editingCarrierEmail && editingCarrierEmail !== newEmail) {
      body.original_email = editingCarrierEmail;
    }
    if (demoMode) {
      setRichCarriers(prev => prev.map(c =>
        c.email === (editingCarrierEmail || newEmail)
          ? { ...c, company_name: carrierForm.name, email: newEmail, contact_name: carrierForm.contact_person, services: carrierForm.services, lanes: parseLanes(carrierForm.tradelanes), vetted: carrierForm.vetted || [] }
          : c
      ));
      setShowEditCarrier(false);
      setSelectedCarrier(null);
      setEditingCarrierEmail(null);
      setCarrierForm({ name: '', email: '', phone: '', contact_person: '', services: [], tradelanes: '' });
      return;
    }
    try {
      const res = await authedFetch(`${settings.addinUrl}/api/carriers/override`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => null);
        window.alert(`Carrier could not be saved: ${detail?.error || res.statusText || 'unknown error'}`);
        return;
      }
    } catch (err) {
      window.alert(`Carrier could not be saved: ${err.message || 'network error'}`);
      return;
    }
    setShowEditCarrier(false);
    setSelectedCarrier(null);
    setEditingCarrierEmail(null);
    setCarrierForm({ name: '', email: '', phone: '', contact_person: '', services: [], tradelanes: '' });
    fetchRichCarriers();
    fetchScoreboard();
  };

  const handleDeleteCarrier = async (email) => {
    if (!window.confirm('Delete this carrier?')) return;
    if (demoMode) {
      setRichCarriers(prev => prev.filter(c => c.email !== email));
      return;
    }
    try {
      const res = await authedFetch(`${settings.addinUrl}/api/carriers?email=${encodeURIComponent(email)}`, { method: 'DELETE' });
      if (!res.ok) {
        const detail = await res.json().catch(() => null);
        window.alert(`Carrier could not be deleted: ${detail?.error || res.statusText || 'unknown error'}`);
        return;
      }
    } catch (err) {
      window.alert(`Carrier could not be deleted: ${err.message || 'network error'}`);
      return;
    }
    fetchRichCarriers();
    fetchScoreboard();
  };

  const handleSelectCarrier = (carrier) => {
    setSelectedCarrier(carrier);
    setEditingCarrierEmail(carrier.email);
    setCarrierForm({
      name: carrier.company_name || '',
      email: carrier.email || '',
      phone: carrier.phone || '',
      contact_person: carrier.contact_name || '',
      services: carrier.services || [],
      tradelanes: (carrier.lanes || []).join(', '),
      hq_country_code: carrier.hq_country_code || '',
      manual_points: carrier.manual_points || 0,
      preferred_lanes: (carrier.preferred_lanes || []).join(', '),
      vetted: Array.isArray(carrier.vetted) ? carrier.vetted : [],
    });
    setLaneInput(''); setVetLane(''); setVetSvc('*');
    setShowEditCarrier(true);
  };

  const toggleService = (svc) => {
    setCarrierForm(f => ({
      ...f,
      services: f.services.includes(svc) ? f.services.filter(s => s !== svc) : [...f.services, svc]
    }));
  };

  const openOrderModal = (rfq, carrier, resp) => {
    const today = new Date().toISOString().split('T')[0];
    const services = carrier?.services || [];
    let trpMode = 'ROAD FREIGHT';
    if (services.includes('AIR')) trpMode = 'AIR FREIGHT';
    else if (services.includes('FCL') || services.includes('LCL')) trpMode = 'SEA FREIGHT';
    else if (services.includes('EXPRESS') && !services.includes('FTL') && !services.includes('LTL') && !services.includes('GROUPAGE')) trpMode = 'EXPRESS SERVICE';
    setSelectedRfq(rfq);
    setPreviewCarrier(carrier || null);
    setPreviewResponse(resp || null);
    // Auto-fill the transport order from the stored background data (the hidden
    // shipment details we kept off the carrier RFQ): exact addresses, contacts,
    // customer/invoice receiver, cargo. Falls back to mapped fields for demo rows.
    const raw = rfq?.raw || {};
    setOrderForm({
      itemName: '',
      orderDate: today,
      status: 'New shipment',
      clientRef: raw.client_reference || raw.client_ref || raw.booking_ref || raw.booking_reference || '',
      pickupCompany: raw.shipper_name || rfq?.origin?.split(',')[0]?.trim() || '',
      pickupAddress: raw.from_address || '',
      pickupCountry: (raw.from_country || rfq?.origin?.split(',')[1]?.trim() || '').toUpperCase(),
      pickupContact: raw.from_contact || '',
      pickupRef: raw.pickup_ref || raw.collection_ref || raw.shipper_ref || '',
      pickupDate: raw.ready_date || '',
      deliveryTo: raw.to_city || rfq?.destination?.split(',')[0]?.trim() || '',
      deliveryAddress: raw.to_address || '',
      deliveryCountry: (raw.to_country || rfq?.destination?.split(',')[1]?.trim() || '').toUpperCase(),
      deliveryContact: raw.to_contact || '',
      deliveryRef: '',
      deliveryDate: raw.delivery_date || '',
      goodsDesc: raw.cargo_public || raw.cargo_summary || '',
      cargoDimensions: raw.dimensions || rfq?.dims || (rfq?.cbm ? `${rfq.cbm}m3` : ''),
      ldm: '',
      weightKg: String(raw.weight_kg || rfq?.kg || ''),
      // Invoice receiver/customer may be neither pickup nor delivery party — keep
      // it visible in comments so it isn't lost (Monday customer set separately).
      comments: [resp?.notes, raw.customer ? `Customer/invoice: ${raw.customer}` : ''].filter(Boolean).join(' | '),
      trpMode,
      carrierInstructions: '',
      carrier: resp?.carrier_name || carrier?.name || '',
      carrierEmail: resp?.carrier_email || carrier?.email || '',
      truckNr: '',
      costEur: String(resp?.all_in_total ?? resp?.price ?? ''),
      rfqNo: rfq?.hp_number || '',
    });
    setShowOrderPreview(true);
  };

  // ── Open Quotes modal: lazy-fetch responses in live mode ──
  const openQuotes = async (rfq) => {
    if (demoMode) {
      setSelectedRfq(rfq);
      setDrawerTab('responses'); setShowResponses(true);
      return;
    }
    try {
      const res = await authedFetch(`${settings.apiUrl}/api/rfq/${rfq.hp_number}/responses`);
      const data = await res.json();
      const mapped = (data.responses || []).map(mapRespFromRaw);
      setSelectedRfq({ ...rfq, responses: mapped });
    } catch {
      setSelectedRfq({ ...rfq, responses: [] });
    }
    setDrawerTab('responses'); setShowResponses(true);
  };

  // Manager scope: 'mine' shows only RFQs this user owns (owner or sending
  // mailbox = their login email); 'all' shows everything. Admins default to 'all'.
  const scopedRfqs = scope === 'all' ? rfqs : rfqs.filter(isMine);
  const filteredRfqs = scopedRfqs.filter(rfq =>
    rfq.hp_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    rfq.origin?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    rfq.destination?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const svcColor = (s) => {
    const map = {
      FTL:     { bg: '#dbeafe', text: '#0F2746' },
      LTL:     { bg: '#e0e7ff', text: '#4338ca' },
      GROUPAGE:{ bg: '#ccfbf1', text: '#0f766e' },
      FCL:     { bg: '#d1fae5', text: '#065f46' },
      LCL:     { bg: '#dcfce7', text: '#166534' },
      AIR:     { bg: '#fef3c7', text: '#92400e' },
      EXPRESS: { bg: '#fee2e2', text: '#991b1b' },
      OOG:     { bg: '#fce7f3', text: '#9d174d' },
      NFO:     { bg: '#f3e8ff', text: '#6b21a8' },
      OBC:     { bg: '#ffedd5', text: '#9a3412' },
      CUSTOMS: { bg: '#cffafe', text: '#155e75' },
    };
    return map[s] || { bg: '#f3f4f6', text: '#374151' };
  };

  const STATUS_PILL = { open: 'st-new', responses_received: 'st-progress', won: 'st-won', lost: 'st-lost', no_quote: 'st-muted' };
  const StatusBadge = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.open;
    return <span className={`pill ${STATUS_PILL[status] || 'st-new'}`}>{cfg.label}</span>;
  };

  // NyxAir-style date "9 Jun 2026"
  const fmtDate = (d) => {
    if (!d) return '';
    const s = String(d).slice(0, 10);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return s;
    return `${+m[3]} ${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][+m[2]-1]} ${m[1]}`;
  };

  // Route as city + 2-letter country chips (table + drawer).
  const RouteChips = ({ rfq }) => {
    const raw = rfq.raw || {};
    const oCity = raw.from_city || (rfq.origin || '').split(',')[0]?.trim() || '—';
    const oCC = (raw.from_country || (rfq.origin || '').split(',')[1] || '').trim().slice(0, 2).toUpperCase();
    const dCity = raw.to_city || (rfq.destination || '').split(',')[0]?.trim() || '—';
    const dCC = (raw.to_country || (rfq.destination || '').split(',')[1] || '').trim().slice(0, 2).toUpperCase();
    return (
      <span className="route">
        {oCity}{oCC && <span className="cc">{oCC}</span>}
        <span className="arrow">→</span>
        {dCity}{dCC && <span className="cc">{dCC}</span>}
      </span>
    );
  };

  const RfqDetails = ({ rfq }) => {
    const raw = rfq.raw || {};
    const rows = [
      ['Reference', rfq.hp_number],
      ['Customer', raw.customer || '—'],
      ['Goods', raw.cargo_public || raw.cargo_summary || '—'],
      ['Dimensions', raw.dimensions || rfq.dims || (rfq.cbm ? `${rfq.cbm} CBM` : '—')],
      ['Weight', (raw.weight_kg || rfq.kg) ? `${raw.weight_kg || rfq.kg} kg` : '—'],
      ['Mode', raw.service_type || '—'],
      ['From', rfq.origin || '—'],
      ['To', rfq.destination || '—'],
      ['Owner', rfq.owner || rfq.created_by || '—'],
      ['Created', fmtDate(raw.created_at) || '—'],
    ];
    return <div>{rows.map(([k, v], i) => <div key={i} className="df"><span className="k">{k}</span><span className="v">{v}</span></div>)}</div>;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  if (ssoBusy) return (
    <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(160deg,#0F2746 0%,#0c2240 100%)', color: '#fff', fontSize: '14px', fontWeight: 600, letterSpacing: '0.3px' }}>
      Completing Microsoft sign-in…
    </div>
  );
  if (!authUser) return <LoginScreen onSignIn={signIn} />;

  return (
    <div className="rfq-dashboard">

      {updateReady && (
        <div
          onClick={() => window.location.reload(true)}
          title="Reload to get the latest version"
          style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 3000, background: '#1E88E5', color: '#fff',
            textAlign: 'center', padding: '8px 12px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(15,39,70,.25)' }}>
          ↻ A new version of the dashboard is available — click here to update
        </div>
      )}

      {/* HEADER */}
      <header className="dashboard-header" style={{ padding: '0 24px 0 0' }}>
        <div className="header-content" style={{ maxWidth: 'none', margin: 0 }}>
          <div className="header-left" style={{ gap: 0, alignItems: 'center' }}>
            <div style={{ padding: '4px 14px', display: 'flex', alignItems: 'center' }}>
              <img
                src="/gt-logo.svg"
                alt="GT-SHIPBROKERS"
                style={{ height: '76px', width: 'auto' }}
              />
            </div>
            <div style={{ padding: '4px 12px 4px 4px', display: 'flex', flexDirection: 'column', justifyContent: 'center', borderLeft: '1px solid #e5e7eb' }}>
              <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', letterSpacing: '0.8px', textTransform: 'uppercase' }}>RFQ MANAGER</span>
            </div>
          </div>

          <div className="header-center">
            <div style={{ display: 'inline-flex', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '999px', padding: '3px' }}>
              {[{ k: 'mine', label: 'My RFQs', n: rfqs.filter(isMine).length }, { k: 'all', label: 'All RFQs', n: rfqs.length }].map(o => (
                <button key={o.k} onClick={() => setScope(o.k)}
                  title={o.k === 'mine' && isAdmin ? 'Admin — switch to All to see every RFQ' : ''}
                  style={{ border: 'none', borderRadius: '999px', padding: '6px 16px', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
                    background: scope === o.k ? '#0F2746' : 'transparent', color: scope === o.k ? '#fff' : '#475569', transition: 'all .15s' }}>
                  {o.label} <span style={{ opacity: 0.7, fontWeight: 600 }}>({o.n})</span>
                </button>
              ))}
            </div>
          </div>

          <div className="header-right">
            {demoMode && (
              <span style={{ padding: '4px 12px', background: '#fef3c7', color: '#92400e', borderRadius: '12px', fontSize: '12px', fontWeight: '700', border: '1px solid #f59e0b' }}>
                DEMO MODE
              </span>
            )}
            <div className="system-status online">
              <span className="status-dot"></span>
              {demoMode ? 'Demo Active' : 'System Online'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingLeft: '12px', marginLeft: '2px', borderLeft: '1px solid #e5e7eb' }}>
              <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: userColor(authUser.email || authUser.name), color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 800, flexShrink: 0 }}>
                {userInitials(authUser.name)}
              </div>
              <div style={{ lineHeight: 1.15 }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#15233b', whiteSpace: 'nowrap' }}>{authUser.name}</div>
                <div style={{ fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.6px', color: isAdmin ? '#b45309' : '#1565c0' }}>{authUser.role}</div>
              </div>
              <button onClick={signOut} className="btn btn-secondary btn-sm" title="Sign out" style={{ marginLeft: '2px' }}>Sign out</button>
            </div>
          </div>
        </div>
      </header>

      <div className="dashboard-layout">
        {/* SIDEBAR */}
        <aside className="sidebar">
          <div style={{ padding: '0 14px 12px' }}>
            <button onClick={() => setShowCreateRFQ(true)} className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}>+ New RFQ</button>
          </div>
          <div className="rail-label">Workspace</div>
          <nav className="nav-menu">
            {[
              { id: 'rfqs',      icon: '📋', label: 'RFQs',      badge: scopedRfqs.filter(r => r.status === 'open' || r.status === 'responses_received').length },
              { id: 'analytics', icon: '📊', label: 'Analytics', badge: 0 },
              { id: 'carriers',  icon: '🚚', label: 'Carriers',  badge: richCarriers.length },
              { id: 'scoreboard', icon: '🏆', label: 'Scoreboard', badge: 0 },
              // Settings (Monday token, API URLs/keys, reminder email body) is admin-only.
              ...(isAdmin ? [{ id: 'settings', icon: '⚙️', label: 'Settings', badge: 0 }] : []),
            ].map(item => (
              <a key={item.id} href={`#${item.id}`}
                className={`nav-item ${activeTab === item.id ? 'active' : ''}`}
                onClick={e => { e.preventDefault(); setActiveTab(item.id); }}
              >
                <span className="nav-icon">{item.icon}</span>
                <span>{item.label}</span>
                {item.badge > 0 && (
                  <span style={{ marginLeft: 'auto', fontSize: '11px', fontWeight: 800, color: 'var(--rail-fg-dim)', background: 'rgba(255,255,255,0.08)', padding: '1px 8px', borderRadius: '999px' }}>
                    {item.badge > 999 ? '999+' : item.badge}
                  </span>
                )}
              </a>
            ))}
          </nav>

          <div style={{ padding: '16px 14px', marginTop: 'auto', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <div style={{ fontSize: '10.5px', color: 'var(--rail-fg-dim)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 800 }}>Connection</div>
            <button
              onClick={() => setDemoMode(d => !d)}
              style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid', borderColor: demoMode ? '#f59e0b' : 'rgba(34,197,94,0.4)', background: demoMode ? 'rgba(245,158,11,0.16)' : 'rgba(34,197,94,0.14)', color: demoMode ? '#fcd34d' : '#86efac', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}
            >
              {demoMode ? '⚡ Demo mode' : '🔗 Live · connected'}
            </button>
          </div>
        </aside>

        {/* MAIN CONTENT */}
        <main className="main-content">

          {/* ── RFQs TAB ── */}
          {activeTab === 'rfqs' && (
            <div className="content-section">
              <div className="section-header">
                <h2>RFQs <span style={{ fontSize: '14px', fontWeight: 700, color: scope === 'mine' ? '#1565c0' : '#94a3b8' }}>· {scope === 'mine' ? 'My RFQs' : 'All RFQs'}</span></h2>
                <p className="section-subtitle">{filteredRfqs.length} requests · {scopedRfqs.filter(r => r.status === 'won').length} won · {scopedRfqs.filter(r => r.status === 'open' || r.status === 'responses_received').length} active</p>
              </div>

              <div className="section-controls">
                <input
                  type="text"
                  placeholder="Search by HP number, origin, destination..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                <button
                  onClick={() => setDemoMode(d => !d)}
                  className="btn btn-sm"
                  style={{ background: demoMode ? '#fef3c7' : '#f0fdf4', color: demoMode ? '#92400e' : '#166534', border: '1px solid', borderColor: demoMode ? '#f59e0b' : '#86efac', fontWeight: '600' }}
                >
                  Demo: {demoMode ? 'ON' : 'OFF'}
                </button>
                <button onClick={() => setShowCreateRFQ(true)} className="btn btn-primary">+ New RFQ</button>
              </div>

              <div className="rfq-list">
                {filteredRfqs.length === 0
                  ? <p className="empty-state">No RFQs found.</p>
                  : (
                    <table className="rfq-table">
                      <thead>
                        <tr>
                          <th style={{ width: '34px' }}></th>
                          <th>RFQ No</th>
                          <th>Status</th>
                          <th>Quotes</th>
                          <th>Route</th>
                          <th>Created</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRfqs.map(rfq => {
                          const wonCarrier = rfq.won_carrier_id ? carriers.find(c => c.id === rfq.won_carrier_id) : null;
                          const contacted = (() => { try { return JSON.parse(rfq.raw?.carriers_contacted || '[]').length; } catch { return 0; } })();
                          const rc = rfq.response_count ?? rfq.responses?.length ?? 0;
                          return (
                            <tr key={rfq.id} onClick={() => openQuotes(rfq)} title="Open RFQ">
                              <td><button className="chev-btn" onClick={e => { e.stopPropagation(); openQuotes(rfq); }} aria-label="Open RFQ">›</button></td>
                              <td className="cell-strong mono">{rfq.hp_number}</td>
                              <td><StatusBadge status={rfq.status} /></td>
                              <td>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                  <span className="mono" style={{ fontWeight: rc > 0 ? 700 : 400, color: rc > 0 ? 'var(--accent)' : 'var(--faint)' }}>{rc}</span>
                                  {contacted > 0 && <span className="pill st-ok" style={{ fontSize: '10.5px', padding: '1px 7px' }}>✓ {rc}/{contacted}</span>}
                                  {wonCarrier && <span style={{ fontSize: '11.5px', color: 'var(--ok)' }}>→ {wonCarrier.name}</span>}
                                </span>
                              </td>
                              <td><RouteChips rfq={rfq} /></td>
                              <td className="cell-date">{fmtDate(rfq.raw?.created_at) || <span className="cell-date empty">—</span>}</td>
                              <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                                {rfq.status === 'won' && (
                                  <button
                                    onClick={() => {
                                      const wonC = rfq.won_carrier_id ? carriers.find(c => c.id === rfq.won_carrier_id) : null;
                                      const wonR = wonC ? (rfq.responses || []).find(r => r.carrier_id === wonC.id) : null;
                                      openOrderModal(rfq, wonC, wonR);
                                    }}
                                    className="btn btn-sm btn-monday"
                                    title="Create the Monday.com transport order for this awarded RFQ"
                                  >→ Monday</button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )
                }
              </div>
            </div>
          )}

          {/* ── ANALYTICS TAB ── */}
          {activeTab === 'analytics' && (
            <div className="content-section">
              <div className="section-header">
                <h2>Analytics <span style={{ fontSize: '14px', fontWeight: 700, color: scope === 'mine' ? '#1565c0' : '#94a3b8' }}>· {scope === 'mine' ? 'My RFQs' : 'All RFQs'}</span></h2>
                <p className="section-subtitle">Pipeline health across {scopedRfqs.length} RFQs</p>
              </div>

              {/* KPI overview strip — compact, fast read of the pipeline */}
              {(() => {
                const total   = scopedRfqs.length;
                const active  = scopedRfqs.filter(r => r.status === 'open' || r.status === 'responses_received').length;
                const respIn  = scopedRfqs.filter(r => r.status === 'responses_received').length;
                const won     = scopedRfqs.filter(r => r.status === 'won').length;
                const lost    = scopedRfqs.filter(r => r.status === 'lost').length;
                const decided = won + lost;
                const winRate = decided > 0 ? Math.round((won / decided) * 100) : 0;
                const wonFrac = decided > 0 ? won / decided : 0;
                const totalQuotes = scopedRfqs.reduce((s, r) => s + (r.response_count ?? r.responses?.length ?? 0), 0);
                const avgQuotes   = total > 0 ? totalQuotes / total : 0;
                const R = 16, CIRC = 2 * Math.PI * R;
                return (
                  <div className="kpi-strip">
                    <div className="kpi-card">
                      <div className="kpi-art">
                        <svg width="40" height="40" viewBox="0 0 40 40" aria-hidden="true">
                          <rect x="6"  y="20" width="6" height="14" rx="2" fill="#bfdbfe"/>
                          <rect x="17" y="12" width="6" height="22" rx="2" fill="#60a5fa"/>
                          <rect x="28" y="6"  width="6" height="28" rx="2" fill="#1565c0"/>
                        </svg>
                      </div>
                      <div className="kpi-figure">
                        <span className="kpi-value">{active}</span>
                        <span className="kpi-label">Open pipeline</span>
                        <span className="kpi-sub">{total} total RFQs</span>
                      </div>
                    </div>

                    <div className="kpi-card">
                      <div className="kpi-art">
                        <svg width="40" height="40" viewBox="0 0 40 40" aria-hidden="true">
                          <circle cx="20" cy="20" r="15" fill="none" stroke="#fde68a" strokeWidth="5"/>
                          <path d="M20 5 a15 15 0 0 1 13 7.5" fill="none" stroke="#d97706" strokeWidth="5" strokeLinecap="round"/>
                        </svg>
                      </div>
                      <div className="kpi-figure">
                        <span className="kpi-value">{respIn}</span>
                        <span className="kpi-label">Quotes in</span>
                        <span className="kpi-sub">awaiting review</span>
                      </div>
                    </div>

                    <div className="kpi-card">
                      <div className="kpi-art">
                        <svg width="44" height="44" viewBox="0 0 44 44" aria-hidden="true">
                          <circle cx="22" cy="22" r={R} fill="none" stroke="#e5e7eb" strokeWidth="6"/>
                          <circle cx="22" cy="22" r={R} fill="none" stroke="#16a34a" strokeWidth="6" strokeLinecap="round"
                            strokeDasharray={CIRC} strokeDashoffset={CIRC * (1 - wonFrac)} transform="rotate(-90 22 22)"/>
                          <text x="22" y="26" textAnchor="middle" fontSize="11" fontWeight="800" fill="#15803d">{winRate}%</text>
                        </svg>
                      </div>
                      <div className="kpi-figure">
                        <span className="kpi-value">{won}</span>
                        <span className="kpi-label">Won</span>
                        <span className="kpi-sub">{decided > 0 ? `${winRate}% win rate` : 'no decisions yet'}</span>
                      </div>
                    </div>

                    <div className="kpi-card">
                      <div className="kpi-art">
                        <svg width="40" height="40" viewBox="0 0 40 40" aria-hidden="true">
                          <path d="M8 12 h24 M8 20 h24 M8 28 h15" stroke="#94a3b8" strokeWidth="3" strokeLinecap="round"/>
                        </svg>
                      </div>
                      <div className="kpi-figure">
                        <span className="kpi-value">{avgQuotes.toFixed(1)}</span>
                        <span className="kpi-label">Avg quotes</span>
                        <span className="kpi-sub">{totalQuotes} received total</span>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Status breakdown */}
              <div className="drawer-sec-title" style={{ marginTop: '20px', marginBottom: '12px' }}>Status breakdown</div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
                  const count = scopedRfqs.filter(r => r.status === key).length;
                  return (
                    <span key={key} className="status-chip" style={{ color: cfg.color, background: cfg.bg, borderColor: `${cfg.color}22`, opacity: count > 0 ? 1 : 0.4 }}>
                      {cfg.label}: {count}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── CARRIERS TAB ── */}
          {activeTab === 'carriers' && (() => {
            const carrierSearch = searchTerm.toLowerCase();
            // Every typed word must appear in name/email/contact, so "bas josa"
            // matches "BAS & JOSA S.L." without needing the exact "&" spelling.
            const searchTokens = carrierSearch.split(/\s+/).filter(Boolean);
            const laneQuery = carrierLaneFilter.trim().toLowerCase();
            const bouncedCount = richCarriers.filter(c => c.bounced).length;
            // Distinct HQ codes present in the data, for the HQ dropdown.
            const hqCodes = Array.from(new Set(
              richCarriers.map(c => (c.hq_country_code || '').toUpperCase()).filter(Boolean)
            )).sort();
            const filtered = richCarriers
              .filter(c => {
                if (!searchTokens.length) return true;
                const hay = `${c.company_name || ''} ${c.email || ''} ${c.contact_name || ''}`.toLowerCase();
                return searchTokens.every(t => hay.includes(t));
              })
              .filter(c => !bouncedOnly || c.bounced)
              .filter(c => !carrierServiceFilter || (c.services || []).includes(carrierServiceFilter))
              .filter(c => !carrierHqFilter || (c.hq_country_code || '').toUpperCase() === carrierHqFilter)
              .filter(c => !laneQuery || (c.lanes || []).some(l => (l || '').toLowerCase().includes(laneQuery)));
            // Ranking points: the add-in's lane-agnostic base (wins ×100, manual
            // boost, vetted +10) plus the +250 preferred-lane bonus when a lane
            // filter is active — mirrors how the add-in scores carriers, so
            // filtering by service/lane + sorting by points shows the top per lane.
            const laneUpper = carrierLaneFilter.trim().toUpperCase();
            const carrierPoints = (c) => {
              let p = Number(c.points || 0);
              if (laneUpper && Array.isArray(c.preferred_lanes)) {
                const from = laneUpper.split('-')[0];
                if (c.preferred_lanes.includes(laneUpper) || (from && c.preferred_lanes.includes(`${from}-*`))) p += 250;
              }
              return p;
            };
            // Sort by chosen column/direction.
            const sortVal = (c) => {
              switch (carrierSort.key) {
                case 'contact_name': return (c.contact_name || '').toLowerCase();
                case 'email':        return (c.email || '').toLowerCase();
                case 'hq_country_code': return (c.hq_country_code || '').toUpperCase();
                case 'services':     return (c.services || []).length;
                case 'lanes':        return (c.lanes || []).length;
                case 'points':       return carrierPoints(c) * 100000 + Number(c.won_count || 0); // tiebreak equal points by win count
                default:             return (c.company_name || '').toLowerCase();
              }
            };
            const sorted = [...filtered].sort((a, b) => {
              const va = sortVal(a), vb = sortVal(b);
              if (va < vb) return carrierSort.dir === 'asc' ? -1 : 1;
              if (va > vb) return carrierSort.dir === 'asc' ? 1 : -1;
              return 0;
            });
            const toggleSort = (key) => setCarrierSort(s =>
              s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'points' ? 'desc' : 'asc' });
            const sortArrow = (key) => carrierSort.key === key ? (carrierSort.dir === 'asc' ? ' ▲' : ' ▼') : '';
            const activeFilters = (carrierServiceFilter ? 1 : 0) + (carrierHqFilter ? 1 : 0) + (laneQuery ? 1 : 0) + (bouncedOnly ? 1 : 0);
            const displayed = sorted.slice(0, 300);
            return (
              <div className="content-section">
                <div className="section-header">
                  <h2>Carriers</h2>
                  <p className="section-subtitle">
                    {richCarriers.length} carriers in database
                    {bouncedCount > 0 && (
                      <span style={{ marginLeft: '10px', color: '#b91c1c', fontWeight: '700' }}>
                        ({bouncedCount} bounced)
                      </span>
                    )}
                  </p>
                </div>

                {carrierFetchError && richCarriers.length === 0 && (
                  <div style={{ margin: '0 0 14px 0', padding: '12px 14px', background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '8px', color: '#92400e', fontSize: '13px', lineHeight: 1.5 }}>
                    {carrierFetchError}
                    {' '}
                    <a href={`${settings.addinUrl}/api/carriers/all`} target="_blank" rel="noreferrer" style={{ color: '#92400e', fontWeight: 700 }}>Open the carrier server →</a>
                  </div>
                )}

                <div className="section-controls" style={{ flexWrap: 'wrap', gap: '8px' }}>
                  <input type="text" placeholder="Search name, email or contact..." className="search-input"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)} />
                  <select className="search-input" style={{ flex: '0 0 auto', minWidth: '140px' }}
                    value={carrierServiceFilter}
                    onChange={e => setCarrierServiceFilter(e.target.value)}>
                    <option value="">All services</option>
                    {ALL_SERVICES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <select className="search-input" style={{ flex: '0 0 auto', minWidth: '120px' }}
                    value={carrierHqFilter}
                    onChange={e => setCarrierHqFilter(e.target.value)}>
                    <option value="">All HQ</option>
                    {hqCodes.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <input type="text" placeholder="Lane e.g. EE-DE" className="search-input"
                    style={{ flex: '0 0 auto', minWidth: '130px' }}
                    value={carrierLaneFilter}
                    onChange={e => setCarrierLaneFilter(e.target.value)} />
                  <button
                    onClick={() => setBouncedOnly(v => !v)}
                    className="btn btn-secondary"
                    style={{ background: bouncedOnly ? '#fee2e2' : undefined, color: bouncedOnly ? '#b91c1c' : undefined, fontWeight: bouncedOnly ? '700' : undefined, borderColor: bouncedOnly ? '#fca5a5' : undefined }}
                  >
                    ⚠ Bounced only
                  </button>
                  {activeFilters > 0 && (
                    <button
                      onClick={() => { setSearchTerm(''); setCarrierServiceFilter(''); setCarrierHqFilter(''); setCarrierLaneFilter(''); setBouncedOnly(false); }}
                      className="btn btn-secondary">
                      Clear filters ({activeFilters})
                    </button>
                  )}
                  <button onClick={() => { setCarrierForm({ name: '', email: '', phone: '', contact_person: '', services: [], tradelanes: '', hq_country_code: '', vetted: [] }); setLaneInput(''); setVetLane(''); setVetSvc('*'); setShowCarrierModal(true); }} className="btn btn-primary">+ Add Carrier</button>
                </div>

                <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 10px 0', fontStyle: 'italic' }}>
                  {filtered.length === richCarriers.length
                    ? `${filtered.length} carriers`
                    : `${filtered.length} of ${richCarriers.length} carriers match`}
                  {filtered.length > 300 && ' — showing first 300, narrow with filters'}
                </p>

                <div className="carrier-list">
                  {displayed.length === 0
                    ? <p className="empty-state">No carriers found.</p>
                    : (
                      <table className="carrier-table">
                        <thead>
                          <tr>
                            <th onClick={() => toggleSort('company_name')} style={{ cursor: 'pointer', userSelect: 'none' }}>Name{sortArrow('company_name')}</th>
                            <th onClick={() => toggleSort('contact_name')} style={{ cursor: 'pointer', userSelect: 'none' }}>Contact{sortArrow('contact_name')}</th>
                            <th onClick={() => toggleSort('email')} style={{ cursor: 'pointer', userSelect: 'none' }}>Email{sortArrow('email')}</th>
                            <th onClick={() => toggleSort('hq_country_code')} style={{ cursor: 'pointer', userSelect: 'none' }}>HQ{sortArrow('hq_country_code')}</th>
                            <th onClick={() => toggleSort('services')} style={{ cursor: 'pointer', userSelect: 'none' }}>Services{sortArrow('services')}</th>
                            <th onClick={() => toggleSort('lanes')} style={{ cursor: 'pointer', userSelect: 'none' }}>Lanes{sortArrow('lanes')}</th>
                            <th onClick={() => toggleSort('points')} style={{ cursor: 'pointer', userSelect: 'none' }} title="Ranking points: wins ×100, manual boost, vetted +10, plus +250 when preferred for the filtered lane. Filter by service/lane then sort to see the top carriers.">Points{sortArrow('points')}</th>
                            <th>Vetted</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* key must be unique — emails are shared across offices; duplicate keys leave stale rows when filtering */}
                          {displayed.map((carrier, idx) => (
                            <tr key={carrier.id || `${carrier.email || ''}|${idx}`}
                              onClick={() => handleSelectCarrier(carrier)}
                              style={{ cursor: 'pointer', ...(carrier.bounced ? { background: '#fef2f2' } : {}) }}
                              title="Click to edit carrier"
                            >
                              <td><strong>{carrier.company_name}</strong></td>
                              <td>{carrier.contact_name || '—'}</td>
                              <td>
                                <a href={`mailto:${carrier.email}`} onClick={e => e.stopPropagation()} style={{color:'#1565c0',textDecoration:'none',fontSize:'13px'}}>{carrier.email}</a>
                                {carrier.bounced && (
                                  <span style={{ marginLeft: '6px', color: '#b91c1c', background: '#fee2e2', padding: '1px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '700' }}>⚠ Bounced</span>
                                )}
                              </td>
                              <td style={{ fontSize: '12px', color: '#4b5563', fontFamily: 'monospace' }}>{carrier.hq_country_code || '—'}</td>
                              <td>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                                  {(carrier.services || []).map(s => (
                                    <span key={s} style={{ padding: '2px 7px', borderRadius: '10px', fontSize: '11px', fontWeight: '700', background: svcColor(s).bg, color: svcColor(s).text }}>
                                      {s}
                                    </span>
                                  ))}
                                </div>
                              </td>
                              <td style={{ fontSize: '12px', color: '#4b5563' }}>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                                  {(carrier.lanes || []).map(t => (
                                    <span key={t} style={{ padding: '2px 6px', borderRadius: '4px', fontSize: '11px', background: '#f3f4f6', color: '#374151', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                      {t}
                                    </span>
                                  ))}
                                </div>
                              </td>
                              <td style={{ fontSize: '13px', fontWeight: 600, color: '#1A2A3A', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                {(() => { const p = carrierPoints(carrier); return p > 0
                                  ? <>{p}{Number(carrier.won_count) > 0 && <span style={{ marginLeft: 4, fontSize: '10.5px', fontWeight: 500, color: '#0D7A45' }} title={`${carrier.won_count} win(s)`}>·{carrier.won_count}w</span>}</>
                                  : <span style={{ color: '#cbd5e1' }}>—</span>; })()}
                              </td>
                              <td style={{ fontSize: '12px' }}>
                                {(carrier.vetted || []).length === 0
                                  ? <span style={{ color: '#cbd5e1' }}>—</span>
                                  : (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                                      {(carrier.vetted || []).slice(0, 4).map((r, i) => (
                                        <span key={i} style={{ padding: '2px 7px', borderRadius: '999px', fontSize: '10.5px', fontWeight: 700, background: '#0F2746', color: '#fff', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                          ★ {r.service === '*' ? '*' : r.service}·{r.lane === '*' ? '*' : r.lane}
                                        </span>
                                      ))}
                                      {(carrier.vetted || []).length > 4 && <span style={{ color: '#64748b', fontSize: '11px' }}>+{(carrier.vetted || []).length - 4}</span>}
                                    </div>
                                  )
                                }
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                                <button onClick={() => handleSelectCarrier(carrier)} className="btn btn-sm btn-secondary" style={{ marginRight: '6px' }}>Edit</button>
                                <button onClick={() => handleDeleteCarrier(carrier.email)} className="btn btn-sm btn-danger">Del</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )
                  }
                </div>
              </div>
            );
          })()}

          {/* ── SCOREBOARD TAB (lane-specific carrier ranking) ── */}
          {activeTab === 'scoreboard' && (
            <div className="content-section">
              <div className="section-header">
                <h2>Lane scoreboard</h2>
                <p className="section-subtitle">
                  The exact order the add-in ranks carriers for a service + lane. Adjust points, prefer or remove carriers to change tomorrow's send order.
                </p>
              </div>

              <div className="section-controls" style={{ flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                <select className="search-input" style={{ flex: '0 0 auto', minWidth: '140px' }}
                  value={sbService} onChange={e => setSbService(e.target.value)}>
                  {ALL_SERVICES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <input type="text" placeholder="From (EE)" className="search-input" maxLength={2}
                  style={{ flex: '0 0 auto', width: '110px', textTransform: 'uppercase', fontFamily: 'monospace', fontWeight: 700 }}
                  value={sbFrom} onChange={e => setSbFrom(e.target.value.toUpperCase())} />
                <button className="btn btn-secondary" title="Swap direction"
                  onClick={() => { const f = sbFrom; setSbFrom(sbTo); setSbTo(f); }}>⇄</button>
                <input type="text" placeholder="To (DE)" className="search-input" maxLength={2}
                  style={{ flex: '0 0 auto', width: '110px', textTransform: 'uppercase', fontFamily: 'monospace', fontWeight: 700 }}
                  value={sbTo} onChange={e => setSbTo(e.target.value.toUpperCase())} />
                {sbReady && (
                  <span style={{ fontSize: '13px', fontWeight: 800, color: '#0F2746', fontFamily: 'monospace' }}>
                    {sbService} · {sbLane}
                  </span>
                )}
                <button className="btn btn-primary" style={{ marginLeft: 'auto' }}
                  onClick={() => {
                    setCarrierForm({ name: '', email: '', phone: '', contact_person: '', services: sbReady ? [sbService] : [], tradelanes: sbReady ? sbLane : '', hq_country_code: '', vetted: [], preferred_lanes: sbReady ? sbLane : '' });
                    setLaneInput(''); setVetLane(''); setVetSvc('*');
                    setShowCarrierModal(true);
                  }}>+ New carrier</button>
              </div>

              {!sbReady && (
                <p className="empty-state" style={{ marginTop: '20px' }}>
                  Pick a service and two-letter From / To countries to load the board — e.g. FTL, EE → DE.
                </p>
              )}

              {sbError && sbReady && (
                <div style={{ margin: '0 0 14px 0', padding: '12px 14px', background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '8px', color: '#92400e', fontSize: '13px' }}>
                  {sbError}
                </div>
              )}

              {sbReady && (
                <>
                  <div style={{ position: 'relative', margin: '0 0 12px 0', maxWidth: '520px' }}>
                    <input type="text" className="search-input" style={{ width: '100%' }}
                      placeholder={`Add carrier from database to ${sbLane}… (search name or email)`}
                      value={sbAddSearch} onChange={e => sbSearchDb(e.target.value)} />
                    {sbAddResults.length > 0 && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', boxShadow: '0 8px 24px rgba(15,39,70,0.12)', overflow: 'hidden' }}>
                        {sbAddResults.map((r, i) => (
                          <div key={`${r.email}|${i}`} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', borderTop: i ? '1px solid #f1f5f9' : 'none' }}>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{ fontSize: '13px', fontWeight: 700, color: '#15233b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {r.company_name} <span style={{ fontWeight: 500, color: '#64748b', fontFamily: 'monospace', fontSize: '11.5px' }}>{r.hq_country_code || ''}</span>
                              </div>
                              <div style={{ fontSize: '11.5px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {r.email} · {(r.services || []).join(' ')}
                              </div>
                            </div>
                            <button className="btn btn-sm btn-primary" style={{ flex: '0 0 auto' }}
                              title={`Mark preferred for ${sbLane} (+250, ranks on top and is auto-selected)`}
                              onClick={() => {
                                const patch = { preferred_lanes: Array.from(new Set([...(r.preferred_lanes || []), sbLane])) };
                                if (!(r.services || []).includes(sbService)) patch.services = [...(r.services || []), sbService];
                                sbSave(r, patch);
                                setSbAddSearch(''); setSbAddResults([]);
                              }}>★ Add to lane</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 10px 0', fontStyle: 'italic' }}>
                    {sbLoading ? 'Loading ranking…' : `${sbRows.length} carriers ranked — top 10 highlighted; ✓ match rows qualify for the add-in's auto-pick${sbRows.length >= 300 ? ' (showing first 300)' : ''}`}
                  </p>

                  <div style={{ maxHeight: '68vh', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '10px' }}>
                    <table className="carrier-table" style={{ margin: 0 }}>
                      <thead>
                        <tr>
                          {['#', 'Carrier', 'Contact', 'Email', 'HQ', 'Score', 'Manual pts', 'Actions'].map(h => (
                            <th key={h} style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 5 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sbRows.length === 0 && !sbLoading
                          ? <tr><td colSpan={8}><p className="empty-state">No carriers offer {sbService} in the database yet — add one with “+ New carrier”.</p></td></tr>
                          : sbRows.map(row => {
                              const topTen = row.rank <= 10;
                              const draft = sbPointsDraft[row.email] ?? String(row.manual_points || 0);
                              const commitPoints = () => {
                                const n = Number(draft);
                                if (!Number.isFinite(n) || n === Number(row.manual_points || 0)) return;
                                sbSave(row, { manual_points: n });
                              };
                              return (
                                <tr key={row.id || `${row.email}|${row.rank}`}
                                  onClick={() => handleSelectCarrier(row)}
                                  style={{ cursor: 'pointer', background: row.bounced ? '#fef2f2' : (topTen ? 'rgba(30,136,229,0.05)' : undefined) }}
                                  title="Click to open the carrier editor"
                                >
                                  <td style={{ whiteSpace: 'nowrap' }}>
                                    {topTen
                                      ? <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '26px', height: '26px', borderRadius: '999px', background: row.rank <= 3 ? '#0F2746' : '#1E88E5', color: '#fff', fontSize: '12.5px', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{row.rank}</span>
                                      : <span style={{ color: '#64748b', fontSize: '13px', fontWeight: 600, fontVariantNumeric: 'tabular-nums', paddingLeft: '8px' }}>{row.rank}</span>}
                                  </td>
                                  <td>
                                    <strong>{row.company_name}</strong>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginTop: '3px' }}>
                                      {row.vetted_for_query && <span style={{ padding: '1px 7px', borderRadius: '999px', fontSize: '10.5px', fontWeight: 700, background: '#0F2746', color: '#fff' }}>★ vetted</span>}
                                      {row.preferred_for_lane && <span style={{ padding: '1px 7px', borderRadius: '999px', fontSize: '10.5px', fontWeight: 700, background: '#dcfce7', color: '#0D7A45' }}>★ lane +250</span>}
                                      {row.recommended && !row.vetted_for_query && !row.preferred_for_lane && <span style={{ padding: '1px 7px', borderRadius: '999px', fontSize: '10.5px', fontWeight: 700, background: '#f1f5f9', color: '#475569' }}>✓ match</span>}
                                      {row.bounced && <span style={{ padding: '1px 7px', borderRadius: '999px', fontSize: '10.5px', fontWeight: 700, background: '#fee2e2', color: '#b91c1c' }}>⚠ bounced</span>}
                                    </div>
                                  </td>
                                  <td>{row.contact_name || '—'}</td>
                                  <td>
                                    <a href={`mailto:${row.email}`} onClick={e => e.stopPropagation()} style={{ color: '#1565c0', textDecoration: 'none', fontSize: '13px' }}>{row.email}</a>
                                  </td>
                                  <td style={{ fontSize: '12px', color: '#4b5563', fontFamily: 'monospace' }}>{row.hq_country_code || '—'}</td>
                                  <td style={{ fontSize: '13.5px', fontWeight: 700, color: '#1A2A3A', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                                    title={(row.reasons || []).join(' · ')}>
                                    {row.score}
                                    {Number(row.won_count) > 0 && <span style={{ marginLeft: 4, fontSize: '10.5px', fontWeight: 500, color: '#0D7A45' }} title={`${row.won_count} win(s)`}>·{row.won_count}w</span>}
                                  </td>
                                  <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                                    <input type="number" className="search-input"
                                      style={{ width: '76px', padding: '4px 8px', fontSize: '13px', fontVariantNumeric: 'tabular-nums' }}
                                      value={draft}
                                      disabled={sbBusy === row.email}
                                      onChange={e => setSbPointsDraft(d => ({ ...d, [row.email]: e.target.value }))}
                                      onBlur={commitPoints}
                                      onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                      title="Manual point boost — type a number and press Enter" />
                                  </td>
                                  <td style={{ whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                                    <button className="btn btn-sm btn-secondary" style={{ marginRight: '6px' }}
                                      disabled={sbBusy === row.email}
                                      title={row.preferred_for_lane ? `Remove the ${sbLane} preference (−250)` : `Prefer for ${sbLane} (+250, ranks on top and is auto-selected)`}
                                      onClick={() => {
                                        const has = row.preferred_for_lane;
                                        const next = has
                                          ? (row.preferred_lanes || []).filter(l => l !== sbLane && l !== `${sbFrom.trim().toUpperCase()}-*`)
                                          : Array.from(new Set([...(row.preferred_lanes || []), sbLane]));
                                        sbSave(row, { preferred_lanes: next });
                                      }}>{row.preferred_for_lane ? '★' : '☆'}</button>
                                    <button className="btn btn-sm btn-secondary" style={{ marginRight: '6px' }} onClick={() => handleSelectCarrier(row)}>Edit</button>
                                    <button className="btn btn-sm btn-danger" onClick={() => handleDeleteCarrier(row.email)}>Del</button>
                                  </td>
                                </tr>
                              );
                            })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── SETTINGS TAB (admin only) ── */}
          {activeTab === 'settings' && !isAdmin && (
            <div className="content-section">
              <div className="section-header">
                <h2>Settings</h2>
                <p className="section-subtitle">Administrator access required</p>
              </div>
              <p className="empty-state">Integration settings and the reminder email template are managed by your administrator.</p>
            </div>
          )}
          {activeTab === 'settings' && isAdmin && (
            <div className="content-section">
              <div className="section-header">
                <h2>Settings</h2>
                <p className="section-subtitle">Monday.com integration & API configuration</p>
              </div>

              <div style={{ maxWidth: '600px' }}>
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
                  <p style={{ margin: 0, fontSize: '13px', color: '#1e40af' }}>
                    <strong>Demo Mode is {demoMode ? 'ON' : 'OFF'}.</strong> {demoMode ? 'Monday.com calls are simulated. Toggle Demo/Live in the sidebar.' : 'Live mode active — Monday.com API will be called on RFQ Won.'}
                  </p>
                </div>

                <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '13px 15px', marginBottom: '20px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#1f2937', marginBottom: '4px' }}>Monday.com — configured on the server</div>
                  <p style={{ margin: 0, fontSize: '12.5px', color: '#6b7280', lineHeight: 1.5 }}>
                    Orders created from the dashboard go through the add-in server using the shared
                    {' '}<code style={{ background: '#eef2f7', padding: '1px 4px', borderRadius: '3px' }}>MONDAY_API_TOKEN</code>.
                    The token is <strong>never stored in any browser</strong> — managers can create orders without it.
                    The target board (<code style={{ background: '#eef2f7', padding: '1px 4px', borderRadius: '3px' }}>MONDAY_BOARD_ID</code>,
                    TEST <code style={{ background: '#eef2f7', padding: '1px 4px', borderRadius: '3px' }}>5091705428</code> by default)
                    is set on the VM <code style={{ background: '#eef2f7', padding: '1px 4px', borderRadius: '3px' }}>.env</code> — flip it to the live board there when ready.
                  </p>
                </div>

                <div className="form-group">
                  <label>Backend API URL (Python)</label>
                  <input
                    type="text"
                    value={settings.apiUrl}
                    onChange={e => setSettings(s => ({ ...s, apiUrl: e.target.value }))}
                    placeholder="http://localhost:8000"
                  />
                </div>

                <div className="form-group">
                  <label>Outlook Add-in URL (Node carrier server)</label>
                  <input
                    type="text"
                    value={settings.addinUrl}
                    onChange={e => setSettings(s => ({ ...s, addinUrl: e.target.value }))}
                    placeholder="http://localhost:3001"
                  />
                  <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#9ca3af' }}>Node server exposing /api/carriers/all — used for carrier database editing</p>
                </div>

                <div className="form-group">
                  <label>API Service Token</label>
                  <input
                    type="password"
                    value={settings.serviceToken}
                    onChange={e => setSettings(s => ({ ...s, serviceToken: e.target.value }))}
                    placeholder="(leave blank in local dev)"
                  />
                  <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#9ca3af' }}>Production only — paste the RFQ_SERVICE_TOKEN value so this dashboard can call the secured backend &amp; add-in. Sent as the <code>X-RFQ-Token</code> header.</p>
                </div>

                <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', marginTop: '8px' }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#1f2937' }}>Integration Flow</h4>
                  <div style={{ fontSize: '13px', color: '#4b5563', lineHeight: '2' }}>
                    <div>1️⃣  Outlook RFQ sent → HP-XXXXX generated</div>
                    <div>2️⃣  Dashboard auto-creates RFQ entry</div>
                    <div>3️⃣  Carriers respond → logged automatically</div>
                    <div>4️⃣  Select best quote → click <strong>✓ Won</strong></div>
                    <div>5️⃣  New item in Monday.com Tellimused board</div>
                    <div>6️⃣  Monday.com sends transport order PDF to carrier</div>
                  </div>
                </div>

                <div style={{ marginTop: '28px', borderTop: '1px solid #e5e7eb', paddingTop: '24px' }}>
                  <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#1f2937' }}>Reminder Email Template</h4>
                  <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#9ca3af' }}>
                    Wording of the follow-up sent from an RFQ's <strong>Responses</strong> tab to carriers who haven't replied (the “Remind” / “Remind all” buttons). The greeting (“Hi,”) and the quoted original RFQ thread are added automatically — this is just the message text.
                  </p>
                  <textarea
                    value={settings.reminderTemplate !== undefined ? settings.reminderTemplate : DEFAULT_REMINDER_TEMPLATE}
                    onChange={e => setSettings(s => ({ ...s, reminderTemplate: e.target.value }))}
                    rows={5}
                    style={{ width: '100%', fontFamily: 'monospace', fontSize: '13px', padding: '10px 12px', borderRadius: '6px', border: '1px solid #d1d5db', resize: 'vertical', boxSizing: 'border-box', lineHeight: '1.6' }}
                  />
                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                    <button
                      onClick={() => setSettings(s => ({ ...s, reminderTemplate: DEFAULT_REMINDER_TEMPLATE }))}
                      className="btn btn-secondary"
                      style={{ fontSize: '12px' }}
                    >
                      Reset to default
                    </button>
                  </div>
                </div>

                <button
                  onClick={() => alert('Settings saved. The reminder wording applies the next time you send a reminder.')}
                  className="btn btn-primary"
                  style={{ marginTop: '20px' }}
                >
                  Save Settings
                </button>
              </div>
            </div>
          )}

        </main>
      </div>

      {/* ═══ MODALS ═══════════════════════════════════════════════════════════ */}

      {/* Create RFQ */}
      {showCreateRFQ && (
        <div className="modal-overlay" onMouseDown={e => { if (e.target === e.currentTarget) setShowCreateRFQ(false); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>New RFQ</h3>
              <button onClick={() => setShowCreateRFQ(false)} className="modal-close">✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>RFQ No (HP number from Outlook)</label>
                <input type="text" value={rfqForm.hp_number}
                  onChange={e => setRfqForm(f => ({ ...f, hp_number: e.target.value }))}
                  placeholder="e.g. HP-00152" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Origin</label>
                  <input type="text" value={rfqForm.origin}
                    onChange={e => setRfqForm(f => ({ ...f, origin: e.target.value }))}
                    placeholder="City, Country" />
                </div>
                <div className="form-group">
                  <label>Destination</label>
                  <input type="text" value={rfqForm.destination}
                    onChange={e => setRfqForm(f => ({ ...f, destination: e.target.value }))}
                    placeholder="City, Country" />
                </div>
              </div>
              <div className="map-container">
                <label>Route Map</label>
                <div ref={mapRef} className="map-view" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Dimensions</label>
                  <input type="text" placeholder="e.g. 120×80×100 cm" value={rfqForm.dims || ''}
                    onChange={e => setRfqForm(f => ({ ...f, dims: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>KG</label>
                  <input type="number" value={rfqForm.kg}
                    onChange={e => setRfqForm(f => ({ ...f, kg: parseFloat(e.target.value) }))} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowCreateRFQ(false)} className="btn btn-secondary">Cancel</button>
              <button onClick={handleCreateRfq} className="btn btn-primary">Create RFQ</button>
            </div>
          </div>
        </div>
      )}

      {/* Quotes / Responses */}
      {/* RFQ detail drawer (slide-out, NyxAir style) */}
      <div className={`drawer-overlay ${showResponses && selectedRfq ? 'open' : ''}`} onClick={() => setShowResponses(false)} />
      {showResponses && selectedRfq && (
        <aside className="rfq-drawer open" aria-label="RFQ detail">

          {/* ── Head ── */}
          <div className="drawer-head">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <span className="gt mono">{selectedRfq.hp_number}</span>
                <StatusBadge status={selectedRfq.status} />
              </div>
              <div style={{ marginTop: '5px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                <RouteChips rfq={selectedRfq} />
              </div>
            </div>
            <button onClick={() => setShowResponses(false)} className="drawer-close" aria-label="Close details">✕</button>
          </div>

          {/* ── Tab bar ── */}
          <div className="drawer-tabs">
            {[
              { id: 'responses', lbl: 'Responses' },
              { id: 'shipment',  lbl: 'Shipment'  },
              { id: 'addquote',  lbl: 'Add Quote' },
            ].map(t => (
              <button key={t.id} className={`drawer-tab ${drawerTab === t.id ? 'active' : ''}`}
                onClick={() => setDrawerTab(t.id)}>{t.lbl}</button>
            ))}
          </div>

          {/* ── Tab bodies ── */}
          <div className="drawer-body">

            {/* ──────── TAB: Responses ──────── */}
            {drawerTab === 'responses' && (
              <div>
                {quoteAddedFor && (
                  <div style={{ marginBottom: '12px', padding: '9px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', color: '#15803d', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '7px' }}>
                    <span>✓</span> Quote from {quoteAddedFor} added to this RFQ.
                  </div>
                )}

              {/* ── Receivers section ── */}
              {(() => {
                const contacted = (() => { try { return JSON.parse(selectedRfq.raw?.carriers_contacted || '[]'); } catch { return []; } })();
                const responses = selectedRfq.responses || [];
                const laneFrom = (selectedRfq.raw?.from_country || '').toUpperCase();
                const laneTo = (selectedRfq.raw?.to_country || '').toUpperCase();
                // A receiver counts as "replied" when the SAME COMPANY answered —
                // matched by email domain, not just the exact address. So a reply
                // from a different person at the company (Eneli vs the contacted
                // Indrek) is credited, and we capture who actually quoted.
                const withReply = contacted.map(c => {
                  const exact = responses.find(r => (r.carrier_email || '').toLowerCase() === (c.email || '').toLowerCase());
                  const dom = baseDomain(c.email);
                  const match = exact || (dom && responses.find(r => baseDomain(r.carrier_email) === dom));
                  const repliedBy = match ? (match.carrier_email || '') : '';
                  return {
                    ...c,
                    replied: !!match,
                    repliedBy,
                    repliedByName: match ? (match.carrier_name || '') : '',
                    // A different person at the same company → offer to learn them.
                    differentPerson: !!repliedBy && repliedBy.toLowerCase() !== (c.email || '').toLowerCase(),
                  };
                });
                const repliedCount = withReply.filter(c => c.replied).length;
                const waitingCount = withReply.filter(c => !c.replied).length;
                const isLearned = (email) => laneContacts.some(lc =>
                  (lc.email || '').toLowerCase() === (email || '').toLowerCase() &&
                  lc.company_domain === baseDomain(email));

                // Carrier-facing recap of the original RFQ (NO addresses / PII) so
                // the follow-up carries context. Built from the stored RFQ fields.
                const buildOriginalRecap = (r) => {
                  if (!r) return '';
                  const L = [];
                  L.push(`Original RFQ — ${selectedRfq.hp_number}`);
                  if (r.service_type) L.push(`Service: ${r.service_type}`);
                  const orig = [r.from_country, r.from_city, r.from_postcode].filter(Boolean).join(' ');
                  const dest = [r.to_country, r.to_city, r.to_postcode].filter(Boolean).join(' ');
                  if (orig || dest) L.push(`Route: ${orig || '?'} → ${dest || '?'}`);
                  const cargo = [
                    r.cargo_public || r.cargo_summary,
                    r.dimensions,
                    (r.weight_kg || r.kg) ? `${r.weight_kg || r.kg} kg` : '',
                    (r.volume_cbm || r.cbm) ? `${r.volume_cbm || r.cbm} cbm` : '',
                  ].filter(Boolean).join(', ');
                  if (cargo) L.push(`Cargo: ${cargo}`);
                  if (r.ready_date) L.push(`Ready: ${r.ready_date}`);
                  if (r.incoterms) L.push(`Incoterms: ${r.incoterms}`);
                  return L.join('\n');
                };

                const nudgeList = async (list) => {
                  if (!selectedRfq.raw?.created_by) {
                    alert('No sender mailbox on this RFQ (it predates sender capture) — re-send it from the add-in to enable nudging.');
                    return;
                  }
                  if (!window.confirm(`Send a follow-up to ${list.length} carrier(s)?`)) return;
                  if (demoMode) {
                    alert('Demo: would nudge ' + list.length);
                    return;
                  }
                  setNudging(true);
                  try {
                    const res = await authedFetch(`${settings.addinUrl}/api/rfq/send`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        sender: selectedRfq.raw.created_by,
                        // No contact_name → the email greets "Hi!" (never a wrong name).
                        // in_reply_to (stored internetMessageId) threads the nudge
                        // under the original RFQ; absent on pre-threading RFQs → fresh send.
                        carriers: list.map(c => ({ email: c.email, in_reply_to: c.message_id || '' })),
                        draft: {
                          subject: 'RE: ' + (selectedRfq.raw?.draft_subject || selectedRfq.hp_number),
                          body: settings.reminderTemplate || DEFAULT_REMINDER_TEMPLATE,
                          quote_original: buildOriginalRecap(selectedRfq.raw),
                        },
                      }),
                    });
                    const data = await res.json();
                    alert(`Nudged ${data.sent}/${data.total}.`);
                  } catch (err) {
                    alert('Nudge failed: ' + err.message);
                  } finally {
                    setNudging(false);
                  }
                };

                if (contacted.length === 0) {
                  return (
                    <div style={{ marginBottom: '16px', padding: '10px 12px', background: '#f9fafb', border: '1px dashed #e5e7eb', borderRadius: '8px', fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>
                      No carriers recorded on this RFQ — nudging not available. (Re-send from the add-in to capture receivers.)
                    </div>
                  );
                }
                return (
                  <div style={{ marginBottom: '16px', padding: '10px 12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '11.5px', fontWeight: '600', color: '#6B84A0', letterSpacing: '.2px' }}>Receivers</span>
                        <span style={{ fontSize: '11px', fontWeight: '700', padding: '1px 8px', borderRadius: '10px',
                          background: repliedCount === contacted.length ? '#dcfce7' : '#eef2f7',
                          color: repliedCount === contacted.length ? '#15803d' : '#475569' }}>
                          {repliedCount}/{contacted.length} replied
                        </span>
                      </span>
                      <button
                        onClick={() => nudgeList(withReply.filter(c => !c.replied))}
                        disabled={waitingCount === 0 || nudging}
                        style={{
                          fontSize: '11px', fontWeight: '500', padding: '4px 11px', borderRadius: '8px',
                          border: '1px solid', cursor: waitingCount === 0 || nudging ? 'not-allowed' : 'pointer',
                          background: '#fff',
                          color: waitingCount === 0 || nudging ? '#9ca3af' : '#1E88E5',
                          borderColor: waitingCount === 0 || nudging ? '#E2E8F1' : '#A3C0ED',
                        }}
                      >
                        {nudging ? 'Sending…' : `Remind all (${waitingCount})`}
                      </button>
                    </div>
                    {(() => {
                      const suggestions = withReply.filter(c => c.replied && c.differentPerson);
                      if (suggestions.length === 0) return null;
                      const pending = suggestions.filter(c => !isLearned(c.repliedBy));
                      return (
                        <div style={{ marginBottom: '10px', padding: '8px 10px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
                            <span style={{ fontSize: '12px', color: '#1e40af', lineHeight: 1.35 }}>
                              {pending.length > 0
                                ? <><strong>{pending.length}</strong> better contact{pending.length > 1 ? 's' : ''} answered for {laneFrom}→{laneTo} — someone other than who you emailed</>
                                : <>All {laneFrom}→{laneTo} contacts learned</>}
                            </span>
                            {pending.length > 1 && (
                              <button
                                onClick={() => confirmLaneContacts(pending.map(c => ({ email: c.repliedBy, name: c.repliedByName, company: c.name })))}
                                disabled={savingAll}
                                style={{
                                  marginLeft: 'auto', flexShrink: 0, fontSize: '11px', fontWeight: '600',
                                  padding: '4px 12px', borderRadius: '8px', border: '1px solid #1E88E5',
                                  background: savingAll ? '#9FC2E8' : '#1E88E5', color: '#fff',
                                  cursor: savingAll ? 'wait' : 'pointer',
                                }}
                              >
                                {savingAll ? 'Saving…' : `Confirm all (${pending.length})`}
                              </button>
                            )}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                            {suggestions.map((c, i) => {
                              const learned = isLearned(c.repliedBy);
                              return (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11.5px', color: '#1e3a8a' }}>
                                  <span style={{ color: '#1E88E5', flexShrink: 0 }}>↪</span>
                                  <span style={{ fontWeight: '600', whiteSpace: 'nowrap' }}>{c.name}</span>
                                  <span style={{ color: '#3b82f6', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {personFromEmail(c.email)} → <strong style={{ color: '#1e40af' }}>{c.repliedByName || personFromEmail(c.repliedBy)}</strong>
                                  </span>
                                  {learned ? (
                                    <span style={{ marginLeft: 'auto', flexShrink: 0, color: '#15803d', fontWeight: '700' }} title={`${laneFrom}→${laneTo} contact`}>✓</span>
                                  ) : (
                                    <button
                                      onClick={() => confirmLaneContact({ email: c.repliedBy, name: c.repliedByName, company: c.name })}
                                      disabled={savingContact === c.repliedBy || savingAll}
                                      style={{
                                        marginLeft: 'auto', flexShrink: 0, fontSize: '10.5px', fontWeight: '600',
                                        padding: '2px 12px', borderRadius: '8px', border: '1px solid #A3C0ED',
                                        background: (savingContact === c.repliedBy || savingAll) ? '#EBF2FB' : '#fff', color: '#1670C4',
                                        cursor: (savingContact === c.repliedBy || savingAll) ? 'wait' : 'pointer',
                                      }}
                                      title={`Set ${c.repliedByName || c.repliedBy} as the ${laneFrom}→${laneTo} contact`}
                                    >
                                      {savingContact === c.repliedBy ? '…' : 'Set'}
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginBottom: '8px' }}>
                      {withReply.map((c, i) => (
                        <div key={i} style={{
                          fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px',
                          padding: '3px 6px', borderRadius: '5px',
                          background: c.replied ? 'transparent' : '#fffaf0',
                        }}>
                          <span style={{ color: c.replied ? '#16a34a' : '#d97706', width: '14px', textAlign: 'center', flexShrink: 0 }}>{c.replied ? '✓' : '⏳'}</span>
                          <span style={{ fontWeight: '600', color: c.replied ? '#15803d' : '#374151', whiteSpace: 'nowrap' }}>{c.name || '—'}</span>
                          <span style={{ color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.email || ''}</span>
                          {!c.replied && (
                            <button
                              onClick={() => nudgeList([c])}
                              disabled={nudging}
                              style={{
                                fontSize: '10.5px', fontWeight: '500', marginLeft: 'auto', flexShrink: 0,
                                padding: '2px 10px', borderRadius: '8px', border: '1px solid #DDE7F2',
                                background: '#fff', color: '#1E88E5',
                                cursor: nudging ? 'not-allowed' : 'pointer',
                              }}
                            >
                              Remind
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize: '11px', color: '#6b7280' }}>
                      Contacted {contacted.length} · Replied {repliedCount} · Waiting {waitingCount}
                    </div>
                  </div>
                );
              })()}

              {/* Existing quotes */}
              <h4 className="drawer-sec-title" style={{ margin: '0 0 12px 0' }}>
                Quotes received ({selectedRfq.responses?.length || 0})
              </h4>
              {selectedRfq.responses?.length > 0 ? (() => {
                // The headline price is the parsed all-in when present, else the
                // manager-entered simple price. Acknowledgements (no figure at all)
                // stay null → "No price yet" and sort last.
                const priceOf = (r) => (r.all_in_total != null ? r.all_in_total : (Number(r.price) > 0 ? Number(r.price) : null));
                const pricedResps = selectedRfq.responses.filter(r => priceOf(r) != null);
                const bestAllIn = pricedResps.length > 0 ? Math.min(...pricedResps.map(priceOf)) : null;

                // Mixed-currency notice
                const currencies = [...new Set(pricedResps.map(r => r.currency).filter(Boolean))];
                const mixedCurrency = currencies.length > 1;

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '24px' }}>
                    {mixedCurrency && (
                      <div style={{ fontSize: '12px', color: '#92400e', background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '6px', padding: '6px 10px' }}>
                        ⚠ Mixed currencies — compare carefully (no auto-conversion).
                      </div>
                    )}
                    {[...selectedRfq.responses]
                      .sort((a, b) => {
                        // Priced responses first (ascending); no-price rows
                        // (acknowledgements) always last.
                        const aVal = priceOf(a) != null ? priceOf(a) : Infinity;
                        const bVal = priceOf(b) != null ? priceOf(b) : Infinity;
                        return aVal - bVal;
                      })
                      .map((resp, idx) => {
                        const carrier = carriers.find(c => c.id === resp.carrier_id);
                        const carrierDisplayName = carrier?.name || resp.carrier_name || '—';
                        const carrierDisplayEmail = carrier?.email || resp.carrier_email || '';
                        const carrierDisplayPhone = carrier?.phone || '';
                        const price = priceOf(resp);
                        const isWon = resp.won || selectedRfq.won_carrier_id === resp.carrier_id;
                        const isCheapest = price != null && price === bestAllIn;
                        const needsReview = price == null || (resp.confidence != null && resp.confidence < 50);

                        // Parse surcharges
                        let charges = [];
                        try { charges = JSON.parse(resp.charges_json || '[]'); } catch { charges = []; }

                        // Determine card border/bg
                        let cardBorder, cardBg, cardStyle = {};
                        if (isWon) { cardBorder = '2px solid #16a34a'; cardBg = '#f0fdf4'; }
                        else if (needsReview) { cardBorder = '1px solid #e5e7eb'; cardBg = '#fafafa'; cardStyle = { opacity: 0.88, borderLeft: '4px solid #f59e0b' }; }
                        else if (isCheapest) { cardBorder = '2px solid #1565c0'; cardBg = '#eff6ff'; }
                        else { cardBorder = '1px solid #e5e7eb'; cardBg = '#fafafa'; }

                        return (
                          <div key={idx} style={{
                            border: cardBorder,
                            borderRadius: '8px', padding: '12px 14px', background: cardBg,
                            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px',
                            ...cardStyle,
                          }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                                <strong style={{ fontSize: '14px' }}>{carrierDisplayName}</strong>
                                {isWon && <span style={{ fontSize: '11px', color: '#16a34a', fontWeight: '700', background: '#dcfce7', padding: '1px 8px', borderRadius: '10px' }}>✓ WON</span>}
                                {isCheapest && !isWon && <span style={{ fontSize: '11px', color: '#1565c0', fontWeight: '700', background: '#dbeafe', padding: '1px 8px', borderRadius: '10px' }}>BEST PRICE</span>}
                                {needsReview && <span style={{ fontSize: '11px', color: '#b45309', fontWeight: '700', background: '#fef3c7', padding: '1px 8px', borderRadius: '10px' }}>REVIEW</span>}
                              </div>
                              <div style={{ fontSize: '12px', color: '#6b7280' }}>{carrierDisplayEmail}{carrierDisplayPhone ? ` · ${carrierDisplayPhone}` : ''}</div>
                              {resp.notes && <div style={{ fontSize: '12px', color: '#4b5563', marginTop: '4px', fontStyle: 'italic' }}>{resp.notes}</div>}
                            </div>
                            <div style={{ textAlign: 'right', whiteSpace: 'nowrap', minWidth: '140px' }}>
                              {/* Price headline (all-in when parsed, else entered price) */}
                              {price != null
                                ? <div style={{ fontSize: '17px', fontWeight: '700', color: '#1f2937' }}>{price} {resp.currency} <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '400' }}>{resp.all_in_total != null ? 'all-in' : 'quoted'}</span></div>
                                : <div style={{ fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>No price yet</div>
                              }
                              {/* Rate line */}
                              {resp.rate_basis && resp.unit_rate != null && (
                                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                                  {resp.rate_basis === 'per_kg'
                                    ? `${resp.unit_rate} ${resp.currency} / kg${resp.chargeable_weight != null ? ` × ${resp.chargeable_weight} kg` : ''}`
                                    : `${resp.unit_rate} ${resp.currency} ${resp.rate_basis.replace(/_/g, ' ')}`
                                  }
                                </div>
                              )}
                              {/* Surcharges */}
                              {charges.length > 0 && (
                                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                                  {charges.map((ch, ci) => (
                                    <div key={ci}>{ch.name}: {ch.amount} {ch.currency}</div>
                                  ))}
                                </div>
                              )}
                              {/* Validity */}
                              {resp.validity_date && (
                                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>valid until {resp.validity_date}</div>
                              )}
                              {/* Transit days */}
                              {resp.transit_days != null && (
                                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{resp.transit_days} days</div>
                              )}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <button
                                onClick={() => openOrderModal(selectedRfq, carrier || { name: resp.carrier_name, email: '' }, resp)}
                                className={`btn btn-sm ${(isWon || isCheapest) ? 'btn-primary' : 'btn-secondary'}`}
                                style={{ whiteSpace: 'nowrap', minWidth: '70px' }}
                              >
                                {isWon ? 'Selected' : 'Select'}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                );
              })() : (
                <p style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '20px' }}>No quotes yet — add one below.</p>
              )}

              </div>
            )}

            {/* ──────── TAB: Add Quote ──────── */}
            {drawerTab === 'addquote' && (
              <div>
              <div className="drawer-sec-title">Add Quote</div>
              <div className="form-group">
                <label>Carrier</label>
                <input
                  type="text"
                  list="quoteCarrierList"
                  value={quoteCarrier}
                  placeholder="Type carrier name or email…"
                  onChange={e => {
                    const val = e.target.value;
                    setQuoteCarrier(val);
                    const emailMatch = val.match(/<([^>]+)>/);
                    if (emailMatch) {
                      const email = emailMatch[1];
                      const name = val.slice(0, val.lastIndexOf(' <')).trim();
                      setResponseForm(f => ({ ...f, carrier_name: name, carrier_email: email }));
                    } else {
                      setResponseForm(f => ({ ...f, carrier_name: val, carrier_email: '' }));
                    }
                  }}
                />
                <datalist id="quoteCarrierList">
                  {richCarriers.slice(0, 1000).map((c, i) => (
                    <option key={i} value={`${c.company_name} <${c.email}>`} />
                  ))}
                </datalist>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Price</label>
                  <input type="number" value={responseForm.price}
                    onChange={e => setResponseForm(f => ({ ...f, price: parseFloat(e.target.value) }))} />
                </div>
                <div className="form-group">
                  <label>Currency</label>
                  <select value={responseForm.currency}
                    onChange={e => setResponseForm(f => ({ ...f, currency: e.target.value }))}>
                    <option>EUR</option><option>USD</option><option>GBP</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Transit Days</label>
                  <input type="number" value={responseForm.transit_days}
                    onChange={e => setResponseForm(f => ({ ...f, transit_days: parseInt(e.target.value) }))} />
                </div>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea value={responseForm.notes} rows="2"
                  onChange={e => setResponseForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Optional notes..." />
              </div>
              <button onClick={handleAddResponse} className="btn btn-secondary" style={{ width: '100%' }}>
                + Add Quote
              </button>

              </div>
            )}

            {/* ──────── TAB: Shipment ──────── */}
            {drawerTab === 'shipment' && (
              <div>
                <RfqDetails rfq={selectedRfq} />
              </div>
            )}

          </div>{/* /drawer-body */}
        </aside>
      )}

      {/* Add Carrier */}
      {showCarrierModal && (
        <div className="modal-overlay" onMouseDown={e => { if (e.target === e.currentTarget) setShowCarrierModal(false); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Carrier</h3>
              <button onClick={() => setShowCarrierModal(false)} className="modal-close">✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Carrier Name</label>
                <input type="text" value={carrierForm.name}
                  onChange={e => setCarrierForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Company name" />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={carrierForm.email}
                  onChange={e => setCarrierForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="rfq@carrier.com" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Contact Person</label>
                  <input type="text" value={carrierForm.contact_person}
                    onChange={e => setCarrierForm(f => ({ ...f, contact_person: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>HQ Country</label>
                  <CountryCombobox
                    value={carrierForm.hq_country_code || ''}
                    onChange={v => setCarrierForm(f => ({ ...f, hq_country_code: v.toUpperCase().slice(0, 2) }))}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Services</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {ALL_SERVICES.map(s => (
                    <label key={s} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer',
                      padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
                      background: carrierForm.services.includes(s) ? svcColor(s).bg : '#f3f4f6',
                      color: carrierForm.services.includes(s) ? svcColor(s).text : '#6b7280',
                      border: carrierForm.services.includes(s) ? `1px solid ${svcColor(s).text}44` : '1px solid #e5e7eb',
                    }}>
                      <input type="checkbox" style={{ display: 'none' }} checked={carrierForm.services.includes(s)} onChange={() => toggleService(s)} />
                      {s}
                    </label>
                  ))}
                </div>
              </div>
              {renderLanesEditor()}
              {renderVettingEditor()}
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowCarrierModal(false)} className="btn btn-secondary">Cancel</button>
              <button onClick={handleCreateCarrier} className="btn btn-primary">Add Carrier</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Carrier */}
      {showEditCarrier && selectedCarrier && (
        <div className="modal-overlay" onMouseDown={e => { if (e.target === e.currentTarget) setShowEditCarrier(false); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit — {selectedCarrier.company_name}</h3>
              <button onClick={() => setShowEditCarrier(false)} className="modal-close">✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Carrier Name</label>
                <input type="text" value={carrierForm.name}
                  onChange={e => setCarrierForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={carrierForm.email}
                  onChange={e => setCarrierForm(f => ({ ...f, email: e.target.value }))} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Contact Person</label>
                  <input type="text" value={carrierForm.contact_person}
                    onChange={e => setCarrierForm(f => ({ ...f, contact_person: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>HQ Country</label>
                  <CountryCombobox
                    value={carrierForm.hq_country_code || ''}
                    onChange={v => setCarrierForm(f => ({ ...f, hq_country_code: v.toUpperCase().slice(0, 2) }))}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Services</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {ALL_SERVICES.map(s => (
                    <label key={s} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer',
                      padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
                      background: carrierForm.services.includes(s) ? svcColor(s).bg : '#f3f4f6',
                      color: carrierForm.services.includes(s) ? svcColor(s).text : '#6b7280',
                      border: carrierForm.services.includes(s) ? `1px solid ${svcColor(s).text}44` : '1px solid #e5e7eb',
                    }}>
                      <input type="checkbox" style={{ display: 'none' }} checked={carrierForm.services.includes(s)} onChange={() => toggleService(s)} />
                      {s}
                    </label>
                  ))}
                </div>
              </div>
              {renderLanesEditor()}
              {renderVettingEditor()}
              <div className="form-group" style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px', marginTop: '6px' }}>
                <label style={{ fontWeight: 700, color: '#92400e' }}>Fine-tuning — points (optional, ranks below vetted)</label>
                <p style={{ margin: '2px 0 8px 0', fontSize: '11px', color: '#9ca3af' }}>
                  Use this once you know who to favour for a lane. Preferred lanes push this carrier to the top of the add-in list and auto-select it; points add a smaller boost.
                </p>
                <label style={{ fontSize: '12px', color: '#6b7280' }}>Preferred lanes (auto-select for these)</label>
                <input type="text" value={carrierForm.preferred_lanes || ''}
                  onChange={e => setCarrierForm(f => ({ ...f, preferred_lanes: e.target.value }))}
                  placeholder="EE-DE, EE-PL  (use EE-* for any from EE)" />
                <label style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px', display: 'block' }}>Manual points (boost)</label>
                <input type="number" value={carrierForm.manual_points || 0}
                  onChange={e => setCarrierForm(f => ({ ...f, manual_points: e.target.value }))}
                  placeholder="0" style={{ maxWidth: '120px' }} />
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => { if (window.confirm('Delete this carrier?')) { handleDeleteCarrier(editingCarrierEmail); setShowEditCarrier(false); setSelectedCarrier(null); setEditingCarrierEmail(null); } }}
                className="btn btn-danger"
                style={{ marginRight: 'auto' }}
              >
                Delete
              </button>
              <button onClick={() => setShowEditCarrier(false)} className="btn btn-secondary">Cancel</button>
              <button onClick={handleUpdateCarrier} className="btn btn-primary">Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* ── ORDER PREVIEW MODAL ── */}
      {showOrderPreview && selectedRfq && (
        <div className="modal-overlay" onMouseDown={e => { if (e.target === e.currentTarget) setShowOrderPreview(false); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>

            {/* Header */}
            <div className="modal-header" style={{ background: '#0F2746', borderRadius: '12px 12px 0 0', flexShrink: 0 }}>
              <div>
                <h3 style={{ margin: 0, color: 'white', fontSize: '16px' }}>Transport Order — Monday.com</h3>
                <p style={{ margin: '3px 0 0 0', color: '#93c5fd', fontSize: '12px' }}>
                  📋 Tellimused &nbsp;→&nbsp; New item &nbsp;|&nbsp; Fill in details below, then confirm
                </p>
              </div>
              <button onClick={() => setShowOrderPreview(false)} className="modal-close" style={{ color: 'white' }}>✕</button>
            </div>

            {/* Scrollable form body */}
            <div className="modal-body" style={{ padding: '0', overflowY: 'auto', flex: 1 }}>

              {/* Auto-generated info bar */}
              <div style={{ background: '#eff6ff', borderBottom: '1px solid #bfdbfe', padding: '8px 20px', display: 'flex', alignItems: 'center', gap: '16px', fontSize: '12px' }}>
                <span style={{ color: '#6b7280' }}>GT Order No:</span>
                <span style={{ fontWeight: '700', color: '#1e40af', fontStyle: 'italic' }}>Auto-generated by Monday.com</span>
                <span style={{ color: '#6b7280', marginLeft: 'auto' }}>RFQ: <strong style={{ color: '#0F2746' }}>{selectedRfq.hp_number}</strong></span>
              </div>

              <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

                {/* ── Section: Order Details ── */}
                <div>
                  <div style={{ fontSize: '10px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px', paddingBottom: '4px', borderBottom: '1px solid #e5e7eb' }}>Order Details</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '11px' }}>Item Name (HP No)</label>
                      <input value={orderForm.itemName} onChange={e => setOrderForm(f => ({ ...f, itemName: e.target.value }))} style={{ fontSize: '13px', fontWeight: '700' }} />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '11px' }}>Order Date</label>
                      <input type="date" value={orderForm.orderDate} onChange={e => setOrderForm(f => ({ ...f, orderDate: e.target.value }))} style={{ fontSize: '13px' }} />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '11px' }}>Status</label>
                      <select value={orderForm.status} onChange={e => setOrderForm(f => ({ ...f, status: e.target.value }))} style={{ fontSize: '13px' }}>
                        {STATUS_LABELS.map(s => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '11px' }}>Trp Mode</label>
                      <select value={orderForm.trpMode} onChange={e => setOrderForm(f => ({ ...f, trpMode: e.target.value }))} style={{ fontSize: '13px' }}>
                        {TRP_MODES.map(m => <option key={m}>{m}</option>)}
                      </select>
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '11px' }}>Client Reference</label>
                      <input value={orderForm.clientRef} onChange={e => setOrderForm(f => ({ ...f, clientRef: e.target.value }))} style={{ fontSize: '13px' }} />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '11px' }}>RFQ No</label>
                      <input value={orderForm.rfqNo} onChange={e => setOrderForm(f => ({ ...f, rfqNo: e.target.value }))} style={{ fontSize: '13px' }} />
                    </div>
                  </div>
                </div>

                {/* ── Section: Pick-up ── */}
                <div>
                  <div style={{ fontSize: '10px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px', paddingBottom: '4px', borderBottom: '1px solid #e5e7eb' }}>Pick-up</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '11px' }}>Pick-up Company</label>
                      <AddressAutocomplete value={orderForm.pickupCompany} onChange={v => setOrderForm(f => ({ ...f, pickupCompany: v }))} onCountryChange={v => setOrderForm(f => ({ ...f, pickupCountry: v }))} style={{ fontSize: '13px' }} />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '11px' }}>Pick-up Country</label>
                      <CountryCombobox
                        value={orderForm.pickupCountry}
                        onChange={v => setOrderForm(f => ({ ...f, pickupCountry: v }))}
                        inputStyle={{ fontSize: '13px', width: '100%' }}
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '11px' }}>Pick-up Date</label>
                      <input type="date" value={orderForm.pickupDate} onChange={e => setOrderForm(f => ({ ...f, pickupDate: e.target.value }))} style={{ fontSize: '13px' }} />
                    </div>
                    <div className="form-group" style={{ margin: 0, gridColumn: 'span 2' }}>
                      <label style={{ fontSize: '11px' }}>Pick-up Address</label>
                      <AddressAutocomplete value={orderForm.pickupAddress} onChange={v => setOrderForm(f => ({ ...f, pickupAddress: v }))} placeholder="Street, City, Postcode" style={{ fontSize: '13px' }} fullAddress />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '11px' }}>Pick-up Contact</label>
                      <input value={orderForm.pickupContact} onChange={e => setOrderForm(f => ({ ...f, pickupContact: e.target.value }))} style={{ fontSize: '13px' }} />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '11px' }}>Pick-up Reference</label>
                      <input value={orderForm.pickupRef} onChange={e => setOrderForm(f => ({ ...f, pickupRef: e.target.value }))} style={{ fontSize: '13px' }} />
                    </div>
                  </div>
                </div>

                {/* ── Section: Delivery ── */}
                <div>
                  <div style={{ fontSize: '10px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px', paddingBottom: '4px', borderBottom: '1px solid #e5e7eb' }}>Delivery</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '11px' }}>Delivery To</label>
                      <AddressAutocomplete value={orderForm.deliveryTo} onChange={v => setOrderForm(f => ({ ...f, deliveryTo: v }))} onCountryChange={v => setOrderForm(f => ({ ...f, deliveryCountry: v }))} style={{ fontSize: '13px' }} />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '11px' }}>Delivery Country</label>
                      <CountryCombobox
                        value={orderForm.deliveryCountry}
                        onChange={v => setOrderForm(f => ({ ...f, deliveryCountry: v }))}
                        inputStyle={{ fontSize: '13px', width: '100%' }}
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '11px' }}>Delivery Date</label>
                      <input type="date" value={orderForm.deliveryDate} onChange={e => setOrderForm(f => ({ ...f, deliveryDate: e.target.value }))} style={{ fontSize: '13px' }} />
                    </div>
                    <div className="form-group" style={{ margin: 0, gridColumn: 'span 2' }}>
                      <label style={{ fontSize: '11px' }}>Delivery Address</label>
                      <AddressAutocomplete value={orderForm.deliveryAddress} onChange={v => setOrderForm(f => ({ ...f, deliveryAddress: v }))} placeholder="Street, City, Postcode" style={{ fontSize: '13px' }} fullAddress />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '11px' }}>Delivery Contact</label>
                      <input value={orderForm.deliveryContact} onChange={e => setOrderForm(f => ({ ...f, deliveryContact: e.target.value }))} style={{ fontSize: '13px' }} />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '11px' }}>Delivery Reference</label>
                      <input value={orderForm.deliveryRef} onChange={e => setOrderForm(f => ({ ...f, deliveryRef: e.target.value }))} style={{ fontSize: '13px' }} />
                    </div>
                  </div>
                </div>

                {/* ── Section: Cargo ── */}
                <div>
                  <div style={{ fontSize: '10px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px', paddingBottom: '4px', borderBottom: '1px solid #e5e7eb' }}>Cargo</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '8px' }}>
                    <div className="form-group" style={{ margin: 0, gridColumn: 'span 2' }}>
                      <label style={{ fontSize: '11px' }}>Goods Description</label>
                      <input value={orderForm.goodsDesc} onChange={e => setOrderForm(f => ({ ...f, goodsDesc: e.target.value }))} style={{ fontSize: '13px' }} />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '11px' }}>Cargo Dimensions (CBM)</label>
                      <input value={orderForm.cargoDimensions} onChange={e => setOrderForm(f => ({ ...f, cargoDimensions: e.target.value }))} style={{ fontSize: '13px' }} />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '11px' }}>LDM</label>
                      <input type="number" value={orderForm.ldm} onChange={e => setOrderForm(f => ({ ...f, ldm: e.target.value }))} style={{ fontSize: '13px' }} />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '11px' }}>Weight (kg)</label>
                      <input type="number" value={orderForm.weightKg} onChange={e => setOrderForm(f => ({ ...f, weightKg: e.target.value }))} style={{ fontSize: '13px' }} />
                    </div>
                    <div className="form-group" style={{ margin: 0, gridColumn: 'span 3' }}>
                      <label style={{ fontSize: '11px' }}>Comments</label>
                      <input value={orderForm.comments} onChange={e => setOrderForm(f => ({ ...f, comments: e.target.value }))} style={{ fontSize: '13px' }} />
                    </div>
                  </div>
                </div>

                {/* ── Section: Carrier ── */}
                <div>
                  <div style={{ fontSize: '10px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px', paddingBottom: '4px', borderBottom: '1px solid #e5e7eb' }}>Carrier</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '11px' }}>Carrier</label>
                      <input value={orderForm.carrier} onChange={e => setOrderForm(f => ({ ...f, carrier: e.target.value }))} style={{ fontSize: '13px', fontWeight: '600' }} />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '11px' }}>Carrier e-mail</label>
                      <input value={orderForm.carrierEmail} onChange={e => setOrderForm(f => ({ ...f, carrierEmail: e.target.value }))} style={{ fontSize: '13px' }} />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '11px' }}>Cost (€)</label>
                      <input type="number" value={orderForm.costEur} onChange={e => setOrderForm(f => ({ ...f, costEur: e.target.value }))} style={{ fontSize: '13px', fontWeight: '700' }} />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '11px' }}>Truck Nr</label>
                      <input value={orderForm.truckNr} onChange={e => setOrderForm(f => ({ ...f, truckNr: e.target.value }))} style={{ fontSize: '13px' }} />
                    </div>
                    <div className="form-group" style={{ margin: 0, gridColumn: 'span 2' }}>
                      <label style={{ fontSize: '11px' }}>Carrier Instructions</label>
                      <input value={orderForm.carrierInstructions} onChange={e => setOrderForm(f => ({ ...f, carrierInstructions: e.target.value }))} style={{ fontSize: '13px' }} />
                    </div>
                  </div>
                </div>

                {/* After creation info */}
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '10px 14px' }}>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: '#166534', marginBottom: '4px' }}>After item is created in Monday.com:</div>
                  <div style={{ fontSize: '11px', color: '#166534', lineHeight: '1.8' }}>
                    ✓ GT order number assigned automatically (GT-XXXXXXX)<br/>
                    ✓ Transport order PDF generated from Monday.com template<br/>
                    ✓ PDF sent automatically to <strong>{orderForm.carrierEmail || '—'}</strong>
                  </div>
                </div>

              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer" style={{ flexShrink: 0 }}>
              <button onClick={() => setShowOrderPreview(false)} className="btn btn-secondary">Close</button>
              <button
                onClick={handleMarkCarrierAsWon}
                disabled={isCreatingMondayItem}
                className="btn btn-primary"
                style={{ background: '#0F2746', minWidth: '260px' }}
              >
                {isCreatingMondayItem ? '⏳ Creating...' : '✓ Confirm — Create Order in Monday.com'}
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default RFQDashboard;
